import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { addDays, getDay } from "date-fns";
import dayjs, { Dayjs } from "dayjs";
import AppButton from "../../UI/AppButtons";
import AppCard from "../../UI/AppCards";
import { AppInputSwitcher } from "../AppInputSwitcher/AppInputSwitcher";
import { LocationSelectWithPanel } from "../LocationSelectWithPanel";
import { AppRadioButton } from "../../UI/AppRadioButtons/AppRadioButtons";
import { AppStandardMultiSelectCheckBox } from "../../UI/AppMultipleSelection/AppMultiSelection";
import BookingApi from "../../../API/Apis/BookingApi";
import {
  AffiliateDetails,
  BookingDetails,
} from "../../../@types/Booking/bookingDetails";
import { BookingFormProps } from "./BookingForm.props";
import BookingGetters from "../../../store/onlineBooking/getters";
import {
  Location,
  LocationOpening,
  Affiliate,
} from "../../../@types/DTO/Locations";
import SearchIcon from "@mui/icons-material/Search";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import TrainIcon from "@mui/icons-material/Train";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import { setTime } from "../../../helpers/dateManipulators";
import { hasValidGpsCoordinates } from "../../../helpers/hasValidGpsCoordinates";
import { useTranslation } from "react-i18next";
import { BookingSourceResponse } from "../../../@types/DTO/BookingSource";
import { t } from "i18next";
import { MacroClassGroup } from "../../../@types/DTO/MacroClass";
import { useSearchParams } from "react-router-dom";
import { formatTheme } from "../../../config/MuiColorThemes";
import { createTheme, Theme, ThemeProvider } from "@mui/material/styles";
import i18n from "../../../config/i18n";
import { BOOKING_ACTIONS } from "../../../store/onlineBooking";
import { useDispatch } from "react-redux";
import { DatePickerComponent } from "../../UI/AppDatePickers/AppDatePicker";
import { CircularProgress } from "@mui/material";
import { BookingFormSkeleton } from "../../UI/BookingFormSkeleton/BookingFormSkeleton";
import { DeliveryOptionsWidget } from "./DeliveryOptionsWidget";
import BranchAddressSearch, { BranchWithDistance } from "./BranchAddressSearch";
import BranchMapPanel, { MapPanelState } from "./BranchMapPanel";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import TwoWheelerIcon from "@mui/icons-material/TwoWheeler";
import { DatePickerComponentNew } from "../../UI/AppDatePickers/AppDatePicker";
import { trackSearch } from "../../../helpers/tracking";

function getEnabledTimeSlots(
  locationArray: Location[],
  locationName: string,
  date: Date | null
) {
  const AppSettings = BookingGetters.getAppSettings();
  const currentTime = new Date();

  const formatDate = (date) => {
    const d = new Date(date);
    d.setHours(currentTime.getHours());
    d.setMinutes(currentTime.getMinutes());
    d.setSeconds(currentTime.getSeconds());
    d.setMilliseconds(currentTime.getMilliseconds());
    return d.toISOString().split("T")[0];
  };

  const locationLang = i18n.language;

  return locationName && date
    ? locationArray
        .filter((el) => {
          const locationValue = AppSettings?.showLocationWebDescription
            ? locationLang === "it" && el.publicWebDescription
              ? el.publicWebDescription
              : locationLang === "en" &&
                el.publicWebDescriptionEN &&
                AppSettings?.showLocationWebDescription
              ? el.publicWebDescriptionEN
              : el.locationName
            : el.locationCode +
              "-" +
              el.locationName +
              " " +
              el.locationNumber +
              " " +
              el.locationAddress +
              " " +
              el.locationCity +
              " " +
              el.zipCode;
          return locationValue.toLowerCase() === locationName.toLowerCase();
        })
        .flatMap((el) => {
          if (el?.isOutOfHours || el?.pickUpOutOfHours) {
            return [
              {
                start: setTime("00:00", date),
                end: setTime("23:59", date),
              },
            ];
          }

          return el.openings
            .filter((e) => {
              const normalizeDate = (d) =>
                new Date(d.toISOString().split("T")[0]);
              let selectedDate = new Date(date);
              let isInValidPeriod = true;
              if (e?.validFrom != null && e?.validTo != null) {
                selectedDate = normalizeDate(selectedDate);
                const validFrom = new Date(e?.validFrom);
                const validTo = new Date(e?.validTo);
                isInValidPeriod =
                  selectedDate >= validFrom && selectedDate <= validTo;
              }
              if (!isInValidPeriod) {
                isInValidPeriod = el.isOutOfHours || isInValidPeriod;
              }

              return (
                isInValidPeriod &&
                (e.dayOfTheWeek % 7 == getDay(date) ||
                  (e.specificDay && e.specificDay == formatDate(date)))
              );
            })
            .map((e) => {
              return {
                start: setTime(e.startTime, date),
                end: setTime(e.endTime, date),
              };
            });
        })
    : [];
}

function getEnabledDropoffTimeSlots(
  locationArray: Location[],
  locationName: string,
  date: Date | null
) {
  const AppSettings = BookingGetters.getAppSettings();
  const currentTime = new Date();
  const formatDate = (date) => {
    const d = new Date(date);
    d.setHours(currentTime.getHours());
    d.setMinutes(currentTime.getMinutes());
    d.setSeconds(currentTime.getSeconds());
    d.setMilliseconds(currentTime.getMilliseconds());
    return d.toISOString().split("T")[0];
  };
  const locationLang = i18n.language;

  if (!locationName || !date) return [];
  const location = locationArray.find((el) => {
    const locationValue = AppSettings?.showLocationWebDescription
      ? locationLang === "it" && el.publicWebDescription
        ? el.publicWebDescription
        : locationLang === "en" &&
          el.publicWebDescriptionEN &&
          AppSettings?.showLocationWebDescription
        ? el.publicWebDescriptionEN
        : el.locationName
      : el.locationCode +
        "-" +
        el.locationName +
        " " +
        el.locationNumber +
        " " +
        el.locationAddress +
        " " +
        el.locationCity +
        " " +
        el.zipCode;
    return locationValue.toLowerCase() === locationName.toLowerCase();
  });

  // If onlyDropOffOutOfHours is true, enable all times
  if (
    location?.onlyDropOffOutOfHours ||
    location?.isOutOfHours ||
    location?.dropOffOutOfHours
  ) {
    return [
      {
        start: setTime("00:00", date),
        end: setTime("23:59", date),
      },
    ];
  }

  // Existing logic for restricted times
  return location
    ? location.openings
        .filter((e) => {
          const normalizeDate = (d) => new Date(d.toISOString().split("T")[0]);
          let selectedDate = new Date(date);
          let isInValidPeriod = true;
          selectedDate = normalizeDate(selectedDate);
          if (e?.validFrom != null && e?.validTo != null) {
            const validFrom = new Date(e?.validFrom);
            const validTo = new Date(e?.validTo);
            isInValidPeriod =
              selectedDate >= validFrom && selectedDate <= validTo;
          }

          if (!isInValidPeriod) {
            isInValidPeriod = location.onlyDropOffOutOfHours || isInValidPeriod;
          }
          return (
            isInValidPeriod &&
            (e.dayOfTheWeek % 7 == getDay(date) ||
              (e.specificDay && e.specificDay == formatDate(date)))
          );
        })
        .map((e) => {
          return {
            start: setTime(e.dropoffStartTime, date),
            end: setTime(e.dropoffendTime, date),
          };
        })
    : [];
}

// function getEnabledDropoffTimeSlots(
//   locationArray: Location[],
//   locationName: string,
//   date: Date | null
// ) {
//   const AppSettings = BookingGetters.getAppSettings();
//   const currentTime = new Date();
//   const formatDate = (date) => {
//     const d = new Date(date);
//     d.setHours(currentTime.getHours());
//     d.setMinutes(currentTime.getMinutes());
//     d.setSeconds(currentTime.getSeconds());
//     d.setMilliseconds(currentTime.getMilliseconds());
//     return d.toISOString().split("T")[0];
//   };
//   const locationLang = i18n.language;
//   return locationName && date
//     ? locationArray
//         .filter((el) => {
//           const locationValue = AppSettings?.showLocationWebDescription
//             ? locationLang === "it" && el.publicWebDescription
//               ? el.publicWebDescription
//               : locationLang === "en" &&
//                 el.publicWebDescriptionEN &&
//                 AppSettings?.showLocationWebDescription
//               ? el.publicWebDescriptionEN
//               : el.locationName
//             : el.locationCode +
//               "-" +
//               el.locationName +
//               " " +
//               el.locationNumber +
//               " " +
//               el.locationAddress +
//               " " +
//               el.locationCity +
//               " " +
//               el.zipCode;
//           return locationValue.toLowerCase() === locationName.toLowerCase();
//         })
//         .flatMap((el) => {
//           return el.openings
//             .filter((e) => {
//               const normalizeDate = (d) =>
//                 new Date(d.toISOString().split("T")[0]);
//               let selectedDate = new Date(date);
//               let isInValidPeriod = true;
//               selectedDate = normalizeDate(selectedDate);
//               if (e?.validFrom != null && e?.validTo != null) {
//                 const validFrom = new Date(e?.validFrom);
//                 const validTo = new Date(e?.validTo);
//                 isInValidPeriod =
//                   selectedDate >= validFrom && selectedDate <= validTo;
//               }
//
//               if (!isInValidPeriod) {
//                 isInValidPeriod = el.onlyDropOffOutOfHours || isInValidPeriod;
//               }
//               return (
//                 isInValidPeriod &&
//                 (e.dayOfTheWeek % 7 == getDay(date) ||
//                   (e.specificDay && e.specificDay == formatDate(date)))
//               );
//             })
//             .map((e) => {
//               return {
//                 start: setTime(e.dropoffStartTime, date),
//                 end: setTime(e.dropoffendTime, date),
//               };
//             });
//         })
//     : [];
// }

/**
 * Haversine formula — returns the great-circle distance in km between two
 * GPS coordinates.  Used to sort pickup locations by proximity to the user's
 * chosen home-delivery address.
 */
function getHaversineDistanceKm(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371; // Earth radius in km
  const toRad = (v: number) => (v * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function BookingForm({
  onSubmit = () => {},
  className = "",
  isEnableHorizontalView,
}: BookingFormProps): JSX.Element {
  const [dateError, setDateError] = useState("");

  const [startDateError, setStartDateError] = useState("");
  const [endDateError, setEndDateError] = useState("");
  const pickupDateGroupRef = useRef<HTMLDivElement>(null);
  const dropoffDateGroupRef = useRef<HTMLDivElement>(null);
  const locationLang = i18n.language;
  const dynamicPalette = formatTheme();
  const dynamicTheme = createTheme(dynamicPalette);
  const { t: translate } = useTranslation();
  const reduxBookingDetails: BookingDetails =
    BookingGetters.getBookingDetails();
  const AppSettings = BookingGetters.getAppSettings();
  const isEmbedRoute = new URL(window.location.href).pathname.includes(
    "/embed"
  );
  const showLocationPanel = AppSettings?.showLocationPanel;
  const maxRentalAllowedDay = AppSettings?.maxRentalDurationInDays || 30;
  const [minRentalAllowedDay] = useState<number>(AppSettings?.minRentDays || 0);
  const [pickupLocation, setPickupLocation] = useState<string>("");
  const [reservationDestination, setReservationDestination] =
    useState<string>("");
  const [macroGroupOptions, setMacroGroupOptions] = useState<MacroClassGroup[]>(
    []
  );
  const [ageCriteriaData, setAgeCriteriaData] = useState([]);
  const [selectedMacroGroup, setSelectedMacroGroup] = useState<string>("");
  const [selectedMacroGroupWebCode, setSelectedMacroGroupWebCode] =
    useState<string>("");
  const [reservationStartDate, setStartDate] = useState<Date | null>(
    reduxBookingDetails.pickUpDateAndTime
      ? new Date(reduxBookingDetails.pickUpDateAndTime)
      : null
  );

  const [reservationEndDate, setEndDate] = useState<Date | null>(
    reduxBookingDetails.dropOffDateAndTime
      ? new Date(reduxBookingDetails.dropOffDateAndTime)
      : null
  );
  const [reservationPlatform, setReservationPlatform] = useState<string>(
    reduxBookingDetails.reservationSource
      ? reduxBookingDetails.reservationSource.name
      : ""
  );
  const [isDroppingAtSameLocation, setIsDroppingAtSameLocation] =
    useState<boolean>(
      reduxBookingDetails.dropAtSameLocation == null ||
        reduxBookingDetails.dropAtSameLocation == true
    );
  const [reservationSourceOptions, setReservationSourceOptions] = useState<
    BookingSourceResponse[]
  >([]);
  const [reseSourceMacroOptions, setReseSourceMacroOptions] = useState<
    BookingSourceResponse[]
  >([]);
  const [isYoungDriverAge, setIsYoungDriverAge] = useState<string>(
    reduxBookingDetails.isYoungDriverAge
      ? reduxBookingDetails.isYoungDriverAge
      : "false"
  );
  const [isSeniorDriverAge, setIsSeniorDriverAge] = useState<string>(
    reduxBookingDetails.isSeniorDriverAge
      ? reduxBookingDetails.isSeniorDriverAge
      : "false"
  );
  const [isNoFeeAge, setIsNoFeeAge] = useState<string>(
    reduxBookingDetails.noFeeAge ? reduxBookingDetails.noFeeAge : "false"
  );
  const [youngDriver2, setYoungDriver2] = useState<string>(
    reduxBookingDetails.isYoungDriver2Age
      ? reduxBookingDetails.isYoungDriver2Age
      : "false"
  );
  const [isAgeLimitSelected, setIsAgeLimitSelected] = useState<string>(
    reduxBookingDetails.isAgeLimitSelected &&
      reduxBookingDetails.isAgeLimitSelected !== "false"
      ? reduxBookingDetails.isAgeLimitSelected
      : "noAgeFeeDriver"
  );

  const [queryParams] = useSearchParams();
  const scriptVoucherCode = queryParams.get("scriptVoucherCode");
  const [agreementCoupon, setagreementCoupon] = useState<string>(
    scriptVoucherCode
      ? scriptVoucherCode
      : reduxBookingDetails.agreementCoupon || ""
  );
  const [disableCoupon, setDisableCoupon] = useState<boolean>(false);
  const [modalMessage, setModalMessage] = useState<string>("");
  const [discountExpireText, setDiscountExpireText] = useState<String>("");
  const [isModalLoading, setIsModalLoading] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);

  // Pickup locations are set manually for home delivery or dropdown selection.
  const [isPickupManuallySelected, setIsPickupManuallySelected] =
    useState<boolean>(
      () =>
        !!(
          reduxBookingDetails.homeDeliverySelected &&
          reduxBookingDetails.deliveryCoordinates
        )
    );
  // dropoff locations are set manually for home return or dropdown selection.
  const [isDropoffManuallySelected, setIsDropoffManuallySelected] =
    useState<boolean>(
      () =>
        !!(
          reduxBookingDetails.homeReturnSelected &&
          reduxBookingDetails.returnCoordinates
        )
    );
  useEffect(() => {
    if (!scriptVoucherCode) return;

    const checkInitialCoupon = async () => {
      setIsModalLoading(true);
      try {
        const channelRes = await BookingApi.getVoucherWithScript(
          scriptVoucherCode
        );

        const isChannelFound = channelRes?.data?.data?.isChannelFound;

        if (isChannelFound === "Y") {
          setDisableCoupon(true);
        } else {
          setDisableCoupon(false);
        }

        const tempStart = reservationStartDate
          ? new Date(reservationStartDate).toISOString()
          : new Date().toISOString();

        const tempEnd = reservationEndDate
          ? new Date(reservationEndDate).toISOString()
          : new Date().toISOString();

        const response = await BookingApi.couponDiscount(
          scriptVoucherCode.trim(),
          tempStart,
          tempEnd,
          0,
          0,
          0,
          0
        );

        const status = response?.data?.data?.bookingStatus;
        const expiredCoupon = response?.data?.data?.msg;

        if (status === "success") {
          setShowModal(false);
        } else if (expiredCoupon === "Agreement coupon is not valid") {
          setModalMessage(translate("attention_discount_expire"));
          setShowModal(true);
        } else {
          setModalMessage(translate("discountNotValidText"));
          setShowModal(true);
        }
      } catch (error) {
        console.error("Coupon validation error:", error);
      } finally {
        setIsModalLoading(false);
      }
    };

    checkInitialCoupon();
  }, [scriptVoucherCode]);

  const [discountValueWithoutVat] = useState<any>(
    reduxBookingDetails.discountValueWithoutVat
      ? reduxBookingDetails.discountValueWithoutVat
      : "0"
  );

  const [locationDetail, setLocationDetail] = useState<Location[]>([]);
  const [locationDropOffDetail, setDropOffLocationDetail] = useState<
    Location[]
  >([]);

  // ─── Home Delivery / Home Return state (lazy-initialised from Redux so state
  //     survives navigation and component remounts without a useEffect delay) ───
  const [homeDeliverySelected, setHomeDeliverySelected] = useState<boolean>(
    () => reduxBookingDetails.homeDeliverySelected ?? false
  );
  const [homeReturnSelected, setHomeReturnSelected] = useState<boolean>(
    () => reduxBookingDetails.homeReturnSelected ?? false
  );
  const [homeDeliveryAddress, setHomeDeliveryAddress] = useState<string>(
    () => reduxBookingDetails.homeDeliveryAddress ?? ""
  );
  const [homeReturnAddress, setHomeReturnAddress] = useState<string>(
    () => reduxBookingDetails.homeReturnAddress ?? ""
  );
  const [deliveryCoordinates, setDeliveryCoordinates] = useState<{
    lat: number;
    lng: number;
  } | null>(() => reduxBookingDetails.deliveryCoordinates ?? null);
  const [returnCoordinates, setReturnCoordinates] = useState<{
    lat: number;
    lng: number;
  } | null>(() => reduxBookingDetails.returnCoordinates ?? null);
  /** locationDetail sorted by ascending distance from the delivery address */
  const [deliverySortedLocations, setDeliverySortedLocations] = useState<
    Location[]
  >([]);
  /** Maps locationCode → distance in km from the delivery address */
  const [locationDistanceMap, setLocationDistanceMap] = useState<
    Record<string, number>
  >({});
  const [returnLocationDistanceMap, setReturnLocationDistanceMap] = useState<
    Record<string, number>
  >({});
  const [returnSortedLocations, setReturnSortedLocations] = useState<
    Location[]
  >([]);
  // ─── Branch address search / map panel state ─────────────────────────────────
  const [branchRankedLocations, setBranchRankedLocations] = useState<
    BranchWithDistance[]
  >([]);
  const [branchSortedLocationList, setBranchSortedLocationList] = useState<
    Location[]
  >([]);
  const [selectedBranchForMap, setSelectedBranchForMap] =
    useState<BranchWithDistance | null>(null);
  const [selectedDropoffBranchForMap, setSelectedDropoffBranchForMap] =
    useState<BranchWithDistance | null>(null);
  const [dropoffRankedLocations, setDropoffRankedLocations] = useState<
    BranchWithDistance[]
  >([]);
  const [dropoffAddressCoords, setDropoffAddressCoords] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  // Branch map search — read from Redux (persists across navigation)
  const {
    branchSearchAddress,
    branchAddressCoords,
    branchDistanceMap,
    branchMapPanelState: reduxMapPanelState,
    branchSelectedLocationCode,
    dropoffSearchAddress,
    dropoffAddressCoords: reduxDropoffAddressCoords,
    dropoffDistanceMap,
    dropoffMapPanelState: reduxDropoffMapPanelState,
    dropoffSelectedLocationCode,
  } = BookingGetters.getBranchMapSearch();
  const [mapPanelState, setMapPanelState] = useState<MapPanelState>(
    (reduxMapPanelState as MapPanelState) ?? 1
  );
  const [dropoffMapPanelState, setDropoffMapPanelState] =
    useState<MapPanelState>((reduxDropoffMapPanelState as MapPanelState) ?? 1);
  const showMapPanel = AppSettings?.showGoogleMapsPanel;
  const [locationsLoaded, setLocationsLoaded] = useState(false);
  const [googleMapsReady, setGoogleMapsReady] = useState(
    () => !!window.google?.maps
  );
  const isInitializing =
    !AppSettings || !locationsLoaded || (showMapPanel && !googleMapsReady);

  useEffect(() => {
    if (!showMapPanel) return;
    if (!locationDetail.length) return;
    if (!Object.keys(branchDistanceMap).length) return;

    const withDist = locationDetail
      .filter((loc) => branchDistanceMap[loc.locationCode] != null)
      .map((loc) => ({
        location: loc,
        distanceKm: branchDistanceMap[loc.locationCode],
        rank: 0,
      }))
      .sort((a, b) => a.distanceKm - b.distanceKm)
      .map((item, index) => ({ ...item, rank: index }));

    setBranchRankedLocations(withDist);
    setBranchSortedLocationList(withDist.map((r) => r.location));

    // saved location code.
    if (reduxMapPanelState === 3 && branchSelectedLocationCode) {
      const restored = withDist.find(
        (r) => r.location.locationCode === branchSelectedLocationCode
      );
      setSelectedBranchForMap(restored ?? null);
      setMapPanelState(restored ? 3 : 2);
      // Mark as manually selected so pickupDisplayValue shows the distance badge.
      if (restored) {
        setIsPickupManuallySelected(true);
      }
    }
  }, [locationDetail, branchDistanceMap]);

  // ─── Restore dropoff ranked list after navigation ─────────────────────────────
  useEffect(() => {
    if (!showMapPanel) return;
    if (!Object.keys(dropoffDistanceMap).length) return;
    const dropoffLocs = locationDropOffDetail.length
      ? locationDropOffDetail
      : locationDetail;
    if (!dropoffLocs.length) return;

    const withDist = dropoffLocs
      .filter((loc) => dropoffDistanceMap[loc.locationCode] != null)
      .map((loc) => ({
        location: loc,
        distanceKm: dropoffDistanceMap[loc.locationCode],
        rank: 0,
      }))
      .sort((a, b) => a.distanceKm - b.distanceKm)
      .map((item, idx) => ({ ...item, rank: idx }));

    setDropoffRankedLocations(withDist);
    setDropoffAddressCoords(reduxDropoffAddressCoords);

    if (dropoffSelectedLocationCode) {
      const restored = withDist.find(
        (r) => r.location.locationCode === dropoffSelectedLocationCode
      );
      setSelectedDropoffBranchForMap(restored ?? null);
      setDropoffMapPanelState(restored ? 3 : 2);
    }
  }, [locationDetail, locationDropOffDetail, dropoffDistanceMap]);
  // ─────────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (googleMapsReady) return;
    const poll = setInterval(() => {
      if (window.google?.maps) {
        setGoogleMapsReady(true);
        clearInterval(poll);
      }
    }, 100);
    const timeout = setTimeout(() => {
      clearInterval(poll);
      setGoogleMapsReady(true);
    }, 10000);
    return () => {
      clearInterval(poll);
      clearTimeout(timeout);
    };
  }, []);

  // ─── Safety timeout: unblock initialization if API calls take too long ───────
  useEffect(() => {
    const timeout = setTimeout(() => {
      setLocationsLoaded(true);
      setGoogleMapsReady(true);
    }, 10000);
    return () => clearTimeout(timeout);
  }, []);

  const [codiceLabel, setCodiceLabel] = useState<String>("");
  const [pageTitleLabel, setPageTitleLabel] = useState<String>("");
  const [searchText, setSearchText] = useState<String>("");
  const [macroGroupLabel, setMacroGroupLabel] = useState("");
  const [bookingReservationSource, setbookingReservationSource] = useState("");
  const [bookingResSourceForReservedArea, setBookingResSourceForReservedArea] =
    useState("");
  const [reservationSourceEnabled, setReservationSourceEnabled] =
    useState(false);
  const [reservationSourceForUser, setReservationSourceForUser] = useState<
    BookingSourceResponse[]
  >([]);
  const [resSourceMacroForUser, setResSourceMacroForUser] = useState<
    BookingSourceResponse[]
  >([]);
  const mountedRef = useRef(false);
  const dropoffUserChangedRef = useRef<boolean>(false);
  const userId: any = localStorage.getItem("userID");
  const isUserValid = userId && userId !== "null" && userId !== "";
  const [reservedAreaPayNow, setReservedAreaPayNow] = useState(false);
  useEffect(() => {
    const fetchResSources = async () => {
      if (!userId || userId == "null") return;
      setReservationSourceEnabled(true);
      const e = await BookingApi.getReservationSourceForOnlineUser(userId);
      const resList = e?.data?.reservationSourceList ?? [];
      const sortedSource = resList
        .filter((item) => item && item.id !== undefined)
        .sort((a, b) => {
          const labelA = (
            a["Ragione Sociale textRS"] ||
            a["Ragione Sociale"] ||
            ""
          ).trim();
          const labelB = (
            b["Ragione Sociale textRS"] ||
            b["Ragione Sociale"] ||
            ""
          ).trim();

          const startsWithNumA = /^\d/.test(labelA);
          const startsWithNumB = /^\d/.test(labelB);

          if (startsWithNumA && startsWithNumB) {
            const numA = parseInt(labelA.match(/^\d+/)[0], 10);
            const numB = parseInt(labelB.match(/^\d+/)[0], 10);

            if (numA !== numB) {
              return numA - numB;
            }
            return labelA.localeCompare(labelB);
          }
          if (startsWithNumA) return -1;
          if (startsWithNumB) return 1;

          return labelA.localeCompare(labelB);
        });
      const resSourceSorted = resList.length > 0 ? sortedSource : [];
      setReservationSourceForUser(resSourceSorted); //
      setResSourceMacroForUser(resSourceSorted);
      const defaultSource =
        resSourceSorted.find((item) => item.isDefaultEnable) ??
        resSourceSorted[0];
      setReservationPlatform(
        defaultSource?.["Ragione Sociale textRS"] ?? defaultSource ?? ""
      );
      mountedRef.current = true;

      if (
        AppSettings?.enablePerDayReservationSource &&
        reservationStartDate &&
        reservationEndDate &&
        AppSettings?.showReservationGroupSelection
      ) {
        const formattedStartDate =
          formatDateInItalianTime(reservationStartDate);
        const formattedEndDate = formatDateInItalianTime(reservationEndDate);

        const start_date_time = reservationStartDate?.getTime() || Date.now();
        const end_date_time =
          reservationEndDate?.getTime() ||
          start_date_time + 24 * 60 * 60 * 1000;
        const startTime = formatTimeInItalianTime(new Date(start_date_time));
        const endTime = formatTimeInItalianTime(new Date(end_date_time));
        const dateResSources =
          await BookingApi.getReservationSourcesOnStartAndEndDate(
            formattedStartDate,
            formattedEndDate,
            locationLang,
            startTime,
            endTime
          );

        const data = dateResSources?.data ?? [];
        if (data.length > 0) {
          let commonReservationSources = data.filter((d: { Codice: string }) =>
            resList.some((u: { code: string }) => u.code === d.Codice)
          );
          if (commonReservationSources.length === 0) {
            commonReservationSources = resList;
          }
          const sortedResSource = commonReservationSources
            .filter((item) => item && item.id !== undefined)
            .sort((a, b) => {
              const labelA = (
                a["Ragione Sociale textRS"] ||
                a["Ragione Sociale"] ||
                ""
              ).trim();
              const labelB = (
                b["Ragione Sociale textRS"] ||
                b["Ragione Sociale"] ||
                ""
              ).trim();

              const startsWithNumA = /^\d/.test(labelA);
              const startsWithNumB = /^\d/.test(labelB);

              if (startsWithNumA && startsWithNumB) {
                const numA = parseInt(labelA.match(/^\d+/)[0], 10);
                const numB = parseInt(labelB.match(/^\d+/)[0], 10);

                if (numA !== numB) {
                  return numA - numB;
                }
                return labelA.localeCompare(labelB);
              }
              if (startsWithNumA) return -1;
              if (startsWithNumB) return 1;

              return labelA.localeCompare(labelB);
            });
          setReservationSourceForUser(sortedResSource);
          setResSourceMacroForUser(sortedResSource);
          const defaultSource =
            sortedResSource.find((item) => item.isDefaultEnable) ??
            sortedResSource[0];
          // console.log("defaultSource", defaultSource);
          setReservationPlatform(
            defaultSource?.["Ragione Sociale textRS"] ?? defaultSource ?? ""
          );
        }
      }
    };
    fetchResSources().catch(console.error);
  }, [userId, reservationStartDate, reservationEndDate]);

  useEffect(() => {
    const isLoggedInUser = userId != null && userId != "null" && userId != "";
    if (reservationPlatform && reservationSourceForUser && isLoggedInUser) {
      const selectedObj = reservationSourceForUser.find(
        (item) =>
          item.description === reservationPlatform ||
          item["Ragione Sociale textRS"] === reservationPlatform
      );
      setReservedAreaPayNow(selectedObj?.payNow ?? false);
    } else if (
      reservationPlatform &&
      !isLoggedInUser &&
      reservationSourceOptions
    ) {
      const selectedObj = reservationSourceOptions.find(
        (item) =>
          item["Ragione Sociale"] === reservationPlatform ||
          item["Ragione Sociale textRS"] === reservationPlatform
      );
      setReservedAreaPayNow(selectedObj?.payNow ?? false);
    }
  }, [reservationPlatform, reservationSourceForUser, reservationSourceOptions]);
  const [leadTimeAlertMsg, setLeadTimeAlertMsg] = useState<any>("");
  const [pickUpDate, setPickUpDate] = useState<String>("");
  const [dropOffDate, setDropOffDate] = useState<String>("");
  const [pickUpTime, setPickUpTime] = useState<String>("");
  const [dropOffTime, setDropOffTime] = useState<String>("");
  const [
    showTextBeginningOfBookingFormMsg,
    setShowTextBeginningOfBookingFormMsg,
  ] = useState<String>("");
  const textResourceDetails =
    BookingGetters.getTextResourceDetailsBasedOnLang();
  const data =
    textResourceDetails?.[locationLang]?.flatMap((item) => item.data || []) ||
    [];
  const [discountNotValidText, setDiscountNotValidText] = useState<String>("");
  const isDesktopScreen = window.innerWidth >= 1310;
  useEffect(() => {
    const bookingSearchTextData = data.find(
      (item) => item.key === "bookingSearchLabel"
    );
    const bookingReservationSourceData = data.find(
      (item) => item.key === "bookingReservationSource"
    );
    const bookingResSourceForReservedAreaData = data.find(
      (item) => item.key === "bookingResSourceForReservedArea"
    );
    const codiceVoucherLabel = data.find(
      (item) => item.key === "codiceVoucherLabel"
    );
    const TitleAtFirstPageLabel = data.find(
      (item) => item.key === "TitleAtFirstPageLabel"
    );
    const macroGroupLabelData = data.find(
      (item) => item.key === "macroGroupLabel"
    );
    const leadTimeAlertMsgData = data.find(
      (item) => item.key === "leadTimeAlertMessage"
    );
    const showTextBeginningOfBookingFormData = data.find(
      (item) => item.key === "showTextBeginningOfBookingFormLable"
    );
    setMacroGroupLabel(
      macroGroupLabelData ? macroGroupLabelData.body : translate("Macro Group")
    );
    setCodiceLabel(codiceVoucherLabel?.body || translate("Voucher Code"));
    setPageTitleLabel(TitleAtFirstPageLabel?.body || t("Movee Car Rental"));
    setSearchText(
      bookingSearchTextData
        ? bookingSearchTextData?.body
        : translate("Find vehicles")
    );
    setLeadTimeAlertMsg(
      leadTimeAlertMsgData?.body ||
        translate("You can start booking after {timeValue}")
    );
    setShowTextBeginningOfBookingFormMsg(
      showTextBeginningOfBookingFormData?.body ||
        translate("show_text_beginning_of_booking_form")
    );
    setbookingReservationSource(
      bookingReservationSourceData
        ? bookingReservationSourceData.body
        : translate("reservation source")
    );
    setBookingResSourceForReservedArea(
      bookingResSourceForReservedAreaData
        ? bookingResSourceForReservedAreaData.body
        : translate("reservation source")
    );

    const pickUpTextData = data.find((item) => item.key === "pickUpDate");
    setPickUpDate(pickUpTextData?.body || t("Pick Up Date"));
    const dropOffTextData = data.find((item) => item.key === "dropOffDate");
    setDropOffDate(dropOffTextData?.body || t("drop off date"));
    const pickUpTimeTextData = data.find((item) => item.key === "pickUpTime");
    setPickUpTime(pickUpTimeTextData?.body || translate("pick up time"));
    const dropOffTimeTextData = data.find((item) => item.key === "dropOffTime");
    setDropOffTime(dropOffTimeTextData?.body || translate("drop off time"));

    const discountExpireTextData = data.find(
      (item) => item.key === "discountExpireText"
    );
    setDiscountExpireText(
      discountExpireTextData?.body || translate("attention_discount_expire")
    );

    const discountNotValidTextData = data.find(
      (item) => item.key === "discountNotValidText"
    );
    setDiscountNotValidText(
      discountNotValidTextData?.body || translate("discountNotValidText")
    );
  }, [data, locationLang, translate]);
  const bookingFormRef = useRef<HTMLFormElement>(null);
  const skeletonRef = useRef<HTMLDivElement>(null);
  function requestParentResize() {
    const iframeMobile = AppSettings?.iframeHeightFirstPageForMobile;
    const iframeDesktop = AppSettings?.iframeHeightForFirstPage;
    const configured = isDesktopScreen ? iframeDesktop : iframeMobile;

    let iframeHeight: number;
    if (configured) {
      iframeHeight = configured;
    } else {
      const el = isInitializing ? skeletonRef.current : bookingFormRef.current;
      if (!el || el.offsetParent === null) return;
      const measured = Math.ceil(
        el.getBoundingClientRect().bottom + window.scrollY + 24
      );
      iframeHeight = isDesktopScreen ? measured : measured + 100;
    }

    window.parent.postMessage(
      {
        event_id: "iframeHeight",
        data: {
          iframeHeight: iframeHeight,
        },
      },
      "*"
    );
  }
  const [isAffiliateEnable, setIsAffiliateEnable] = useState<boolean>(false);
  const [smallScreen] = useState(window.innerWidth <= 768);
  const istabletScreen = window.innerWidth > 768 && window.innerWidth < 1300;
  const [filterViaMacro, setFilterViaMacro] = useState(false);

  useEffect(() => {
    setIsAffiliateEnable(AppSettings?.enableAffiliate ?? false);
    setFilterViaMacro(AppSettings?.fonteViaMacroClass ?? false);
  }, [AppSettings]);

  useEffect(() => {
    if (AppSettings ? !AppSettings.showInIframe : !isEmbedRoute) return;
    // instead of measuring the iframe height once (while the wrong page was loaded),
    // this code watches the form and keeps the iframe sized to whatever is actually on screen.
    let raf = 0;
    const measure = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => requestParentResize());
    };
    measure();
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => measure());
      if (bookingFormRef.current) ro.observe(bookingFormRef.current);
      if (skeletonRef.current) ro.observe(skeletonRef.current);
    }
    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
    };
  }, [AppSettings?.showInIframe, isInitializing, isEmbedRoute]);
  const [oldResStartDate, setOldResStartDate] = useState<Date | null>();
  const [oldResEndDate, setOldResEndDate] = useState<Date | null>();
  const currentTime = new Date();
  const formatDate = (date) => {
    const d = new Date(date);
    d.setHours(currentTime.getHours());
    d.setMinutes(currentTime.getMinutes());
    d.setSeconds(currentTime.getSeconds());
    d.setMilliseconds(currentTime.getMilliseconds());
    return d.toISOString().split("T")[0];
  };
  useEffect(() => {
    const openingSchedule: LocationOpening[] = [];
    const resStartDate =
      reservationStartDate != null ? new Date(reservationStartDate) : null;
    const resEndDate =
      reservationEndDate != null ? new Date(reservationEndDate) : null;
    if (resStartDate != null && resEndDate != null) {
      resStartDate.setHours(0, 0, 0, 0);
      resEndDate.setHours(0, 0, 0, 0);
      if (
        resStartDate.getTime() != oldResStartDate?.getTime() ||
        resEndDate.getTime() != oldResEndDate?.getTime()
      ) {
        setOldResStartDate(resStartDate);
        setOldResEndDate(resEndDate);
        for (const loc of locationDetail) {
          if (
            loc.locationName === reservationDestination ||
            loc.publicWebDescription == reservationDestination ||
            loc.publicWebDescriptionEN == reservationDestination
          ) {
            loc.openings.forEach((time) => {
              if (
                time.dayOfTheWeek % 7 == reservationEndDate?.getDay() ||
                (time.specificDay &&
                  time.specificDay == formatDate(reservationStartDate))
              ) {
                openingSchedule.push(time);
              }
            });
            break;
          }
        }
        for (const openings of openingSchedule) {
          if (
            openings?.isValidPeriod == "false" ||
            openings?.isValidPeriod == "null"
          ) {
            let dropOffTime;
            if (resStartDate.getTime() === resEndDate.getTime()) {
              dropOffTime =
                openings?.LocationStartTime || openings?.dropoffendTime;
            } else {
              dropOffTime =
                openings?.LocationStartTime || openings?.dropoffStartTime;
            }
            setEndDate(setTime(dropOffTime, reservationEndDate || new Date()));
            if (openings?.startTime > reservationStartDate?.getTime()) {
              setStartDate(
                setTime(
                  openings?.LocationStartTime || openings?.startTime,
                  reservationStartDate || new Date()
                )
              );
            }
            // setStartDate(
            //   setTime(
            //     openings?.LocationStartTime || openings?.startTime,
            //     reservationStartDate || new Date()
            //   )
            // );
            break;
          } else if (
            openings?.validTo != null &&
            openings?.validFrom != null &&
            reservationEndDate &&
            reservationEndDate?.getTime() >
              new Date(openings?.validFrom).getTime() &&
            reservationEndDate?.getTime() <
              new Date(openings?.validTo).getTime()
          ) {
            let dropOffTime;
            if (resStartDate.getTime() === resEndDate.getTime()) {
              dropOffTime =
                openings?.LocationStartTime || openings?.dropoffendTime;
            } else {
              dropOffTime =
                openings?.LocationStartTime || openings?.dropoffStartTime;
            }
            setEndDate(setTime(dropOffTime, reservationEndDate || new Date()));
            if (openings?.startTime > reservationStartDate?.getTime()) {
              setStartDate(
                setTime(
                  openings?.LocationStartTime || openings?.startTime,
                  reservationStartDate || new Date()
                )
              );
            }
            // setStartDate(
            //   setTime(
            //     openings?.LocationStartTime || openings?.startTime,
            //     reservationStartDate || new Date()
            //   )
            // );
            break;
          }
        }
        const currentDateWithoutTime = new Date();
        currentDateWithoutTime.setHours(0, 0, 0, 0);
        if (resStartDate.getTime() === currentDateWithoutTime.getTime()) {
          const currentDateWithTime = new Date();
          let hour = currentDateWithTime.getHours();
          const minutes = currentDateWithTime.getMinutes() < 30 ? 30 : 0;
          hour += currentDateWithTime.getMinutes() < 30 ? 0 : 1;
          setStartDate(
            setTime(hour + ":" + minutes, reservationStartDate || new Date())
          );
          if (resStartDate.getTime() == resEndDate.getTime()) {
            setEndDate(setTime(hour + 1 + ":" + 0, resEndDate || new Date()));
          }
        }
      }
    }
  }, [reservationStartDate, reservationEndDate]);

  useEffect(() => {
    BookingApi.getBookingAgeCriteria(locationLang).then((response) => {
      const data = response?.data;
      if (data) {
        Object.keys(data).forEach((key) => {
          if (
            key === "youngDriver" ||
            key === "noAgeDriver" ||
            key === "seniorDriver" ||
            key === "youngDriver2"
          ) {
            setAgeCriteriaData((prevState) => ({
              ...prevState,
              [key]: data[key],
            }));
          }
        });
        if (data.selectYoungDriver) {
          setIsAgeLimitSelected("youngDriver");
        } else if (data.selectYoungDriver2) {
          setIsAgeLimitSelected("youngDriver2");
        } else if (data.selectNoAgeDriver) {
          setIsAgeLimitSelected("noAgeFeeDriver");
        } else if (data.selectSeniorDriver) {
          setIsAgeLimitSelected("seniorDriver");
        }
      }
    });
  }, []);

  useEffect(() => {
    const affiliateDetails = reduxBookingDetails.affiliateDetails;
    const macroClassDetails = reduxBookingDetails.macroGroupName;
    const resSourceDetails = reduxBookingDetails.reservationSource.name;
    if (affiliateDetails) {
      setSelectedAffiliateName(affiliateDetails.name);
      setSelectedAffiliate(affiliateDetails.code);
    } else {
      setSelectedAffiliateName("");
    }
    if (macroClassDetails && userId == null) {
      setSelectedMacroGroup(macroClassDetails);
      setSelectedMacroGroupWebCode(macroClassDetails);
    }
    if (resSourceDetails && userId == null) {
      setReservationPlatform(resSourceDetails);
    }
    if (AppSettings?.showLocationWebDescription) {
      const locationDetails = reduxBookingDetails.pickUpLocationDetails;
      if (locationDetails?.name) {
        const description =
          locationLang === "it"
            ? locationDetails.publicWebDescription || locationDetails.name
            : locationLang === "en"
            ? locationDetails.publicWebDescriptionEN || locationDetails.name
            : "";

        setPickupLocation(description);
      }
    } else {
      if (reduxBookingDetails.pickUpLocationDetails.name) {
        setPickupLocation(
          reduxBookingDetails.pickUpLocationDetails.code +
            "-" +
            reduxBookingDetails.pickUpLocationDetails.name +
            " " +
            reduxBookingDetails.pickUpLocationDetails.locationNumber +
            " " +
            reduxBookingDetails.pickUpLocationDetails.locationAddress +
            " " +
            reduxBookingDetails.pickUpLocationDetails.locationCity +
            " " +
            reduxBookingDetails.pickUpLocationDetails.zipCode
        );
      }
    }
    if (!dropoffUserChangedRef.current) {
      if (AppSettings?.showLocationWebDescription) {
        const locationDetails = reduxBookingDetails.dropOffLocationDetails;
        if (locationDetails?.name) {
          const description =
            locationLang === "it"
              ? locationDetails.publicWebDescription || locationDetails.name
              : locationLang === "en"
              ? locationDetails.publicWebDescriptionEN || locationDetails.name
              : "";

          setReservationDestination(description);
        }
      } else {
        if (reduxBookingDetails.dropOffLocationDetails.name) {
          setReservationDestination(
            reduxBookingDetails.dropOffLocationDetails.code +
              "-" +
              reduxBookingDetails.dropOffLocationDetails.name +
              " " +
              reduxBookingDetails.dropOffLocationDetails.locationNumber +
              " " +
              reduxBookingDetails.dropOffLocationDetails.locationAddress +
              " " +
              reduxBookingDetails.dropOffLocationDetails.locationCity +
              " " +
              reduxBookingDetails.dropOffLocationDetails.zipCode
          );
        }
      }
    }
  }, [AppSettings, reduxBookingDetails, locationLang]);
  useEffect(() => {
    if (isAgeLimitSelected === "youngDriver") {
      setIsYoungDriverAge("true");
      setIsSeniorDriverAge("false");
      setIsNoFeeAge("false");
      setYoungDriver2("false");
    } else if (
      isAgeLimitSelected === "noAgeFeeDriver" ||
      isAgeLimitSelected === "noAgeDriver"
    ) {
      setIsYoungDriverAge("false");
      setIsSeniorDriverAge("false");
      setIsNoFeeAge("true");
      setYoungDriver2("false");
    } else if (isAgeLimitSelected === "seniorDriver") {
      setIsYoungDriverAge("false");
      setIsSeniorDriverAge("true");
      setIsNoFeeAge("false");
      setYoungDriver2("false");
    } else if (isAgeLimitSelected === "youngDriver2") {
      setIsYoungDriverAge("false");
      setIsSeniorDriverAge("false");
      setIsNoFeeAge("false");
      setYoungDriver2("true");
    } else {
      setIsYoungDriverAge("false");
      setIsSeniorDriverAge("false");
      setIsNoFeeAge("false");
      setYoungDriver2("false");
    }
  }, [isAgeLimitSelected]);
  const userAffiliate = localStorage.getItem("userAffiliate");
  const [selectedAffiliate, setSelectedAffiliate] = useState<string>();
  const [selectedAffiliateName, setSelectedAffiliateName] =
    useState<string>("");

  const scriptAffiliate = queryParams.get("scriptAffiliate");
  const [affiliateDetail, setAffiliateDetail] = useState<Affiliate[]>([]);
  const affiliateOptions = affiliateDetail.map((e) => {
    const optionValue = e[1];
    const icon = <LocationCityIcon htmlColor="#1860ab" />;
    return {
      label: optionValue,
      value: optionValue,
      icon,
    };
  });
  const scriptAffiliateIndex = Number(scriptAffiliate);
  useEffect(() => {
    if (isAffiliateEnable) {
      BookingApi.getAffiliate().then((data: Record<string, any>) => {
        const userId: any = localStorage.getItem("userID");
        let attachedOnlineAffiliates = data.data.affiliates;
        // Exclude specific affiliates
        attachedOnlineAffiliates = attachedOnlineAffiliates.filter(
          (affiliate: any) =>
            affiliate[1] !== "Dogma Systems" &&
            affiliate[1] !== "WORLD APP. SRL"
        );
        // If scriptAffiliate parameter exists, filter by affiliateId
        if (
          scriptAffiliate !== null &&
          !isNaN(scriptAffiliateIndex) &&
          scriptAffiliate != ""
        ) {
          const affiliateIdToFind = Number(scriptAffiliate);
          const selectedAffiliate = attachedOnlineAffiliates.find(
            (affiliate: any) => affiliate[0] === affiliateIdToFind
          );
          if (selectedAffiliate && selectedAffiliate !== "undefined") {
            sessionStorage.setItem("scriptAffiliate", scriptAffiliate);
          }
          if (selectedAffiliate) {
            setAffiliateDetail([selectedAffiliate]);
            setSelectedAffiliate(selectedAffiliate[0]);
            setSelectedAffiliateName(selectedAffiliate[1]);
            return;
          }
        }

        if (
          userId != null &&
          userId != "null" &&
          userId != "" &&
          userAffiliate != null
        ) {
          const filteredAffiliates = attachedOnlineAffiliates.filter(
            (affiliate: any) => affiliate[0] == userAffiliate
          );
          setSelectedAffiliate(userAffiliate);
          setSelectedAffiliateName(filteredAffiliates[0][1]);
        } else {
          setAffiliateDetail(attachedOnlineAffiliates || []);
        }
      });
    }
  }, [isAffiliateEnable]);
  const [isInvalidScriptChannelResSource, setIsInvalidScriptChannelResSource] =
    useState<boolean>(false);
  const [isScriptChannelResSource, setIsScriptChannelResSource] =
    useState<boolean>(false);
  const scriptChannel = queryParams.get("scriptChannel");
  const locInfo = BookingGetters.getAffiliateLocationDetails();
  const dispatch = useDispatch();
  const fetchLocationApi = async (affiliateId: string) => {
    try {
      const data: Record<string, any> =
        await BookingApi.getReservationLocations(affiliateId);
      const locationError = data.data.status == "error";
      if (locationError) {
        setLocationDetail([]);
        setLocationsLoaded(true);
        return;
      }
      let sortByLocationName;
      if (AppSettings?.enableLocationInReverseOrder) {
        sortByLocationName = (a, b) => {
          return b.locationName.localeCompare(a.locationName);
        };
      } else {
        sortByLocationName = (a, b) => {
          return a.locationName.localeCompare(b.locationName);
        };
      }
      const sortedLocation = data.data.sort(sortByLocationName);
      // Update local states
      if (
        AppSettings?.locationBasedOnPickUpAndDropoff &&
        !isAffiliateEnable &&
        AppSettings?.locationReturnEnable
      ) {
        const sortedPickUpLocation = sortedLocation.filter(
          (e) => e.locationType == 3 || e.locationType == 1
        );
        setLocationDetail(sortedPickUpLocation || []);
        const sortedDropOffUpLocation = sortedLocation.filter(
          (e) => e.locationType == 3 || e.locationType == 2
        );
        setDropOffLocationDetail(sortedDropOffUpLocation || []);
      } else {
        setLocationDetail(sortedLocation || []);
        setDropOffLocationDetail(sortedLocation || []);
      }
      setLocationsLoaded(true);
      // Dispatch to Redux
      if (isAffiliateEnable && !AppSettings?.locationBasedOnPickUpAndDropoff) {
        dispatch(
          BOOKING_ACTIONS.setAffiliateLocationDetails({
            [affiliateId]: sortedLocation || [],
          })
        );
      }
    } catch (error) {
      console.error("Error fetching locations", error);
      setLocationsLoaded(true);
    }
  };
  function formatTimeInItalianTime(date: any) {
    return new Intl.DateTimeFormat("it-IT", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false, // ensures 24-hour format
    }).format(date);
  }

  function formatDateInItalianTime(date: any) {
    const formatted = new Intl.DateTimeFormat("it-IT", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
      .format(date)
      .split("/")
      .reverse()
      .join("-");
    return formatted;
  }
  const [formattedStartDate, setFormattedStartDate] = useState("");
  const [formattedEndDate, setFormattedEndDate] = useState("");
  useEffect(() => {
    const start_date_time = reservationStartDate?.getTime() || Date.now();
    const end_date_time =
      reservationEndDate?.getTime() || start_date_time + 24 * 60 * 60 * 1000;
    const start_date = new Date(start_date_time);
    const end_date = new Date(end_date_time);
    const formattedStartDate = formatDateInItalianTime(start_date);
    const formattedEndDate = formatDateInItalianTime(end_date);
    setFormattedStartDate(formattedStartDate);
    setFormattedEndDate(formattedEndDate);
  }, [reservationStartDate, reservationEndDate]);

  const fetchResSourceApi = async (affiliateId: string) => {
    try {
      const data = (await BookingApi.getAllReservationSources(
        affiliateId || ""
      )) as BookingSourceResponse[];
      if (data && data.length > 0) {
        const sortedResSource = data
          .filter((item) => item && item.Id !== undefined)
          .sort((a, b) => {
            const labelA = (
              a["Ragione Sociale textRS"] ||
              a["Ragione Sociale"] ||
              ""
            ).trim();
            const labelB = (
              b["Ragione Sociale textRS"] ||
              b["Ragione Sociale"] ||
              ""
            ).trim();

            const startsWithNumA = /^\d/.test(labelA);
            const startsWithNumB = /^\d/.test(labelB);

            if (startsWithNumA && startsWithNumB) {
              const numA = parseInt(labelA.match(/^\d+/)[0], 10);
              const numB = parseInt(labelB.match(/^\d+/)[0], 10);

              if (numA !== numB) {
                return numA - numB;
              }
              return labelA.localeCompare(labelB);
            }
            if (startsWithNumA) return -1;
            if (startsWithNumB) return 1;

            return labelA.localeCompare(labelB);
          });
        setReservationSourceOptions(sortedResSource);
        setReseSourceMacroOptions(sortedResSource);
      }
      if (
        AppSettings?.enablePerDayReservationSource &&
        userId == null &&
        AppSettings?.showReservationGroupSelection
      ) {
        const start_date_time = reservationStartDate?.getTime() || Date.now();
        const end_date_time =
          reservationEndDate?.getTime() ||
          start_date_time + 24 * 60 * 60 * 1000;
        let startTime = formatTimeInItalianTime(new Date(start_date_time));
        let endTime = formatTimeInItalianTime(new Date(end_date_time));
        if (reservationEndDate == null) {
          startTime = "";
          endTime = "";
        }
        const e = (await BookingApi.getReservationSourcesOnStartAndEndDate(
          formattedStartDate,
          formattedEndDate,
          locationLang,
          startTime,
          endTime
        )) as BookingSourceResponse[];
        const data = e?.data;
        const withoutStartEndResSource =
          (await BookingApi.getAllReservationSources(
            affiliateId || ""
          )) as BookingSourceResponse[];
        const checkData = data.length > 0 ? data : withoutStartEndResSource;
        const sortedResSource = checkData
          .filter(
            (item) => item && (item.id !== undefined || item.Id !== undefined)
          )
          .sort((a, b) => {
            const labelA = (
              a["Ragione Sociale textRS"] ||
              a["Ragione Sociale"] ||
              ""
            ).trim();
            const labelB = (
              b["Ragione Sociale textRS"] ||
              b["Ragione Sociale"] ||
              ""
            ).trim();

            const startsWithNumA = /^\d/.test(labelA);
            const startsWithNumB = /^\d/.test(labelB);

            if (startsWithNumA && startsWithNumB) {
              const numA = parseInt(labelA.match(/^\d+/)[0], 10);
              const numB = parseInt(labelB.match(/^\d+/)[0], 10);

              if (numA !== numB) {
                return numA - numB;
              }
              return labelA.localeCompare(labelB);
            }
            if (startsWithNumA) return -1;
            if (startsWithNumB) return 1;

            return labelA.localeCompare(labelB);
          });
        if (reservationEndDate && reservationStartDate) {
          setReservationSourceOptions(sortedResSource);
          setReseSourceMacroOptions(sortedResSource);

          if (
            sortedResSource.length === 0 ||
            !sortedResSource.some(
              (option: any) =>
                (option["Ragione Sociale textRS"] ||
                  option["Ragione Sociale"]) === reservationPlatform
            )
          ) {
            setReservationPlatform("");
          }
        }
      }
    } catch (error) {
      console.error("Error fetching reservation sources", error);
    }
  };

  useEffect(() => {
    const resSourceDetails = reduxBookingDetails.reservationSource.name;
    if (resSourceDetails == "" && !scriptChannel) {
      if (userId == null) {
        const foundOption = reservationSourceOptions.find(
          (e) => e["Ragione Sociale textRS"]
        );
        const res = foundOption ? foundOption["Ragione Sociale textRS"] : "";
        setReservationPlatform(res);
      } else {
        const foundOption = reservationSourceForUser?.find(
          (e) => e["description"]
        );
        const res = foundOption ? foundOption["description"] : "";
        setReservationPlatform(res);
      }
      if (reservationSourceOptions.length > 0) {
        const resSourceData = reservationSourceOptions.find(
          (e) => e.isDefaultEnable === true
        );
        if (resSourceData) {
          setReservationPlatform(resSourceData["Ragione Sociale textRS"]);
        }
      }
      if (reservationSourceForUser.length > 0) {
        const resSourceData = reservationSourceForUser.find(
          (e) => e.isDefaultEnable === true
        );
        if (resSourceData) {
          setReservationPlatform(resSourceData["Ragione Sociale textRS"]);
        }
      }
    }
  }, [reservationSourceOptions, reservationSourceForUser]);
  useEffect(() => {
    // if (isAffiliateEnable === undefined) return;
    const affiliateId = selectedAffiliate?.toString() || "";
    const debounceTimeout = setTimeout(() => {
      const existingLocationDetails = locInfo && locInfo[affiliateId];
      if (isAffiliateEnable && selectedAffiliate) {
        //console.log("if condition excuting");
        if (existingLocationDetails) {
          //console.log("From redux");
          fetchResSourceApi(affiliateId);
          setLocationDetail(existingLocationDetails || []);
          setLocationsLoaded(true);
        } else {
          //console.log("Not From redux");
          fetchLocationApi(affiliateId);
          fetchResSourceApi(affiliateId);
        }
      } else if (
        (selectedAffiliate == undefined || selectedAffiliate == "null") &&
        (isAffiliateEnable == false || isAffiliateEnable == null)
      ) {
        fetchLocationApi("");
        fetchResSourceApi("");
      }
    }, 10);
    return () => clearTimeout(debounceTimeout);
  }, [
    selectedAffiliate,
    isAffiliateEnable,
    isEmbedRoute,
    locInfo,
    locationLang,
    reservationEndDate,
    reservationStartDate,
  ]);

  useEffect(() => {
    if (filterViaMacro && selectedMacroGroup) {
      const fetchData = async () => {
        try {
          const data = (await BookingApi.getAllMacroReservationSources(
            selectedMacroGroup || ""
          )) as BookingSourceResponse[];

          const reservationSource = data
            .filter((item) => item?.Id !== undefined)
            .map((item) => item.Id);
          if (
            selectedAffiliate &&
            isAffiliateEnable &&
            reservationSourceOptions
          ) {
            if (isUserValid && resSourceMacroForUser?.length > 0) {
              const commonSources = reseSourceMacroOptions.filter((option) =>
                data.some((d) => d.Id === option.Id)
              );
              setReservationSourceForUser(commonSources);
            } else {
              setReservationSourceOptions(
                reseSourceMacroOptions.filter((stable) =>
                  data.some((res) => stable.Id === res.Id)
                )
              );
            }
          } else {
            if (isUserValid && resSourceMacroForUser?.length > 0) {
              const filterSources = (sourceList: any[]) =>
                sourceList.filter((stable) =>
                  reseSourceMacroOptions.some((res) => stable.id === res.Id)
                );
              setReservationSourceForUser(filterSources(resSourceMacroForUser));
            } else {
              setReservationSourceOptions(
                reservationSource.length > 0 ? data : []
              );
            }
          }
        } catch (error) {
          console.error("Error fetching reservation sources:", error);
        }
      };

      fetchData();
    }
  }, [
    selectedMacroGroup,
    filterViaMacro,
    isAffiliateEnable,
    selectedAffiliate,
    userId,
  ]);

  useEffect(() => {
    BookingApi.getAllMacroGroups().then((e) => {
      setMacroGroupOptions(e);
    });
  }, []);
  const companyCode = process.env.REACT_APP_COMPANY_CODE == "loverent";
  // ----  set reservationSource from the query parameters for rentalPlusBooking  ----
  useEffect(() => {
    const userId: any = localStorage.getItem("userID");
    if (!userId && scriptChannel && reservationSourceOptions?.length > 0) {
      const reservationSource =
        reservationSourceOptions?.find((e) => e["Codice"] === scriptChannel)?.[
          "Ragione Sociale textRS"
        ] || "";
      if (reservationSource && reservationSource !== "undefined") {
        window.name = scriptChannel; //storing the scriptChannel even when page get refreshed
        sessionStorage.setItem("scriptChannel", scriptChannel);
        setIsScriptChannelResSource(true);
        setReservationPlatform(reservationSource);
      } else {
        setIsInvalidScriptChannelResSource(true);
        setIsScriptChannelResSource(true);
      }
    }
  }, [reservationSourceOptions]);
  // ----------------------- END ----------------------------------

  const validateAgreementCoupon = AppSettings?.validateAgreementCoupon;
  const rateAmount = 0;
  const optionalAmount = 0;
  const totalAmount = 0;
  const refIdContext = 0;

  const scrollToTop = () => {
    window.parent.postMessage(
      {
        event_id: "scrollToTop",
      },
      "*"
    );
  };
  // Validation function for discount code with API call
  const validateDiscountCode = async (code) => {
    try {
      const response = await BookingApi.couponDiscount(
        code.trim(),
        new Date(reservationStartDate).toISOString(),
        new Date(reservationEndDate).toISOString(),
        rateAmount,
        optionalAmount,
        totalAmount,
        refIdContext
      );

      const responseData = response?.data;
      const nestedData = responseData?.data;
      const status = responseData?.status;
      const bookingStatus = nestedData?.bookingStatus;
      if (status && status !== "error") {
        if (bookingStatus == "error") {
          scrollToTop();
          setShowModal(true);
          setModalMessage(discountExpireText);
          // setModalMessage(translate("attention_discount_expire"));
          return false;
        } else {
          console.log("Coupon applied successfully!");
          return true;
        }
      } else {
        scrollToTop();
        setShowModal(true);
        setModalMessage(discountNotValidText);
        // setModalMessage(
        //   translate(
        //     "discountNotValidText"
        //   )
        // );
        return false;
      }
    } catch (error) {
      console.error("Error applying coupon:", error);
      setShowModal(true);
      return false;
    }
  };
  // ─── Sort pickup locations by distance when delivery coords change ───────────
  useEffect(() => {
    if (
      !homeDeliverySelected ||
      !deliveryCoordinates ||
      !locationDetail.length
    ) {
      // Reset to default order when delivery is not active or coords are missing
      setDeliverySortedLocations([]);
      setLocationDistanceMap({});
      return;
    }

    const { lat, lng } = deliveryCoordinates;

    // Only sort locations that have valid GPS data
    const withDistances = locationDetail
      .filter(hasValidGpsCoordinates)
      .map((loc) => ({
        loc,
        distanceKm: getHaversineDistanceKm(
          lat,
          lng,
          loc.latitude,
          loc.longitude
        ),
      }));

    // Locations without GPS coordinates go to the end
    const withoutCoords = locationDetail.filter(
      (loc) => !hasValidGpsCoordinates(loc)
    );

    const sorted = [...withDistances].sort(
      (a, b) => a.distanceKm - b.distanceKm
    );

    setDeliverySortedLocations([
      ...sorted.map(({ loc }) => loc),
      ...withoutCoords,
    ]);

    const distMap: Record<string, number> = {};
    sorted.forEach(({ loc, distanceKm }) => {
      distMap[loc.locationCode] = distanceKm;
    });
    setLocationDistanceMap(distMap);
  }, [homeDeliverySelected, deliveryCoordinates, locationDetail]);

  useEffect(() => {
    if (!homeReturnSelected || !returnCoordinates || !locationDetail.length) {
      setReturnSortedLocations([]);
      setReturnLocationDistanceMap({});
      return;
    }
    const { lat, lng } = returnCoordinates;
    const withDistances = locationDetail
      .filter(hasValidGpsCoordinates)
      .map((loc) => ({
        loc,
        dist: getHaversineDistanceKm(lat, lng, loc.latitude, loc.longitude),
      }))
      .sort((a, b) => a.dist - b.dist);
    const withoutCoords = locationDetail.filter(
      (loc) => !hasValidGpsCoordinates(loc)
    );
    setReturnSortedLocations([
      ...withDistances.map((m) => m.loc),
      ...withoutCoords,
    ]);
    const distMap: Record<string, number> = {};
    withDistances.forEach((m) => {
      distMap[m.loc.locationCode] = m.dist;
    });
    setReturnLocationDistanceMap(distMap);
  }, [homeReturnSelected, returnCoordinates, locationDetail]);
  // ─── Map panel state is driven exclusively by BranchAddressSearch ────────────
  // ─────────────────────────────────────────────────────────────────────────────

  const [pickupLoc, setPickupLoc] = useState<string>("");
  const [dropupLocation, setDropupLocation] = useState<string>("");
  const headerAndFooter = AppSettings?.headerAndFooter;
  useEffect(() => {
    // Store the current value to avoid referencing outdated state
    const locDetails = pickupLoc;

    if (
      !locDetails ||
      !headerAndFooter ||
      !AppSettings?.showLocationWebDescription
    ) {
      return;
    }
    const location = locationDetail?.find(
      (e: Location) =>
        locDetails === e.publicWebDescriptionEN ||
        locDetails === e.publicWebDescription ||
        locDetails === e.locationName
    );

    const dropSource =
      dropupLocation || (isDroppingAtSameLocation ? locDetails : "");
    const DropUpLocation = dropSource
      ? locationDetail?.find(
          (e: Location) =>
            dropSource === e.publicWebDescriptionEN ||
            dropSource === e.publicWebDescription ||
            dropSource === e.locationName
        )
      : undefined;
    if (location && locationLang) {
      const updatedLocation =
        locationLang === "en"
          ? location.publicWebDescriptionEN || location.locationName
          : location.publicWebDescription || location.locationName;

      if (updatedLocation !== pickupLocation) {
        setPickupLocation(updatedLocation);
      }
    }
    const validLocation =
      DropUpLocation ?? (isDroppingAtSameLocation ? location : undefined);
    if (validLocation && locationLang) {
      const updatedLocation =
        locationLang === "en"
          ? validLocation.publicWebDescriptionEN || validLocation?.locationName
          : validLocation.publicWebDescription || validLocation?.locationName;
      if (updatedLocation !== reservationDestination) {
        setReservationDestination(updatedLocation);
      }
    }
  }, [
    pickupLocation,
    locationDetail,
    headerAndFooter,
    dropupLocation,
    reservationDestination,
    locationLang,
    isDroppingAtSameLocation,
  ]);

  // ─── Home Delivery computed values ───────────────────────────────────────────

  const effectivePickupLocations: Location[] =
    homeDeliverySelected &&
    deliveryCoordinates != null &&
    deliverySortedLocations.length > 0
      ? deliverySortedLocations
      : showMapPanel &&
        branchAddressCoords != null &&
        branchSortedLocationList.length > 0
      ? branchSortedLocationList
      : locationDetail;
  // Dropoff locations sorted by distance from the branch-search address.
  const effectiveDropoffLocations: Location[] =
    homeReturnSelected &&
    returnCoordinates != null &&
    returnSortedLocations.length > 0
      ? returnSortedLocations
      : showMapPanel &&
        dropoffAddressCoords != null &&
        dropoffRankedLocations.length > 0
      ? dropoffRankedLocations.map((r) => r.location)
      : locationDropOffDetail;

  const effectiveDropoffMapState: MapPanelState = reservationDestination
    ? dropoffMapPanelState
    : dropoffAddressCoords != null
    ? 2
    : 1;
  const effectiveDropoffSelectedBranch = reservationDestination
    ? selectedDropoffBranchForMap
    : null;

  // Flag used reactive-wise to determine when to trigger the Dropoff Location Selector view next to Pickup
  const isHomeReturnWithAddressActive =
    homeReturnSelected && !!homeReturnAddress;
  const getLocationOptionValue = useCallback(
    (e: Location): string =>
      AppSettings?.showLocationWebDescription
        ? locationLang === "it" && e.publicWebDescription
          ? e.publicWebDescription
          : locationLang === "en" && e.publicWebDescriptionEN
          ? e.publicWebDescriptionEN
          : e.locationName
        : `${e.locationCode}-${e.locationName} ${e.locationNumber} ${e.locationAddress} ${e.locationCity} ${e.zipCode}`,
    [AppSettings, locationLang]
  );

  /** O(1) locationCode -> distanceKm lookup, replacing a per-option .find() scan. */
  const dropoffRankedDistanceMap = useMemo(() => {
    const map: Record<string, number> = {};
    dropoffRankedLocations.forEach((r) => {
      map[r.location.locationCode] = r.distanceKm;
    });
    return map;
  }, [dropoffRankedLocations]);

  /** Single source of truth for "how far is this pickup location", used by both the label suffix and the sort order. */
  const getPickupDistanceKm = useCallback(
    (e: Location): number | null => {
      if (homeDeliverySelected && deliveryCoordinates != null) {
        const d = locationDistanceMap[e.locationCode];
        if (d != null) return d;
      }
      if (showMapPanel && branchAddressCoords != null) {
        const d = branchDistanceMap[e.locationCode];
        if (d != null) return d;
      }
      return null;
    },
    [
      homeDeliverySelected,
      deliveryCoordinates,
      locationDistanceMap,
      showMapPanel,
      branchAddressCoords,
      branchDistanceMap,
    ]
  );

  /** Single source of truth for "how far is this dropoff location", used by both the label suffix and the sort order. */
  const getDropoffDistanceKm = useCallback(
    (e: Location): number | null => {
      if (showMapPanel && dropoffAddressCoords != null) {
        const d = dropoffRankedDistanceMap[e.locationCode];
        if (d != null) return d;
      }
      return null;
    },
    [showMapPanel, dropoffAddressCoords, dropoffRankedDistanceMap]
  );

  const getPickupLocationLabel = useCallback(
    (e: Location): string => {
      const baseName = getLocationOptionValue(e);
      const distKm = getPickupDistanceKm(e);
      return distKm != null
        ? `${baseName} — ${distKm.toFixed(1)} km`
        : baseName;
    },
    [getLocationOptionValue, getPickupDistanceKm]
  );

  const getDropoffLocationLabel = useCallback(
    (e: Location): string => {
      const baseName = getLocationOptionValue(e);
      const distKm = getDropoffDistanceKm(e);
      return distKm != null ? `${baseName} — ${distKm} km` : baseName;
    },
    [getLocationOptionValue, getDropoffDistanceKm]
  );
  /** Return-reactive: same clean value + distance suffix used pre-BOOKING-984. */
  const getReturnOptionLabel = useCallback(
    (e: Location): string => {
      const cleanValue = getLocationOptionValue(e);
      const distance = returnLocationDistanceMap[e.locationCode];
      return distance != null
        ? `${cleanValue} — ${distance.toFixed(1)} KM`
        : cleanValue;
    },
    [getLocationOptionValue, returnLocationDistanceMap]
  );

  const getPickupDistanceSortValue = useCallback(
    (e: Location): number => getPickupDistanceKm(e) ?? Number.POSITIVE_INFINITY,
    [getPickupDistanceKm]
  );

  const getDropoffDistanceSortValue = useCallback(
    (e: Location): number =>
      getDropoffDistanceKm(e) ?? Number.POSITIVE_INFINITY,
    [getDropoffDistanceKm]
  );

  const getReturnDistanceSortValue = useCallback(
    (e: Location): number => {
      const d = returnLocationDistanceMap[e.locationCode];
      return d != null ? d : Number.POSITIVE_INFINITY;
    },
    [returnLocationDistanceMap]
  );

  /** Shared onChange handler for the plain "Drop-off Location" selector
   * (key="dropOffLocation", rendered when !isDroppingAtSameLocation), used by
   * every layout branch's LocationSelectWithPanel/AppInputSwitcher variant so
   * a future fix to this logic only needs to land once instead of being
   * hand-copied per branch. */
  const handleDropoffChange = useCallback(
    (e: string) => {
      dropoffUserChangedRef.current = true;
      setReservationDestination(e);
      setDropupLocation(e);
      setStartDate(null);
      setEndDate(null);
      if (showMapPanel && dropoffAddressCoords) {
        const matched = dropoffRankedLocations.find(
          (r) => getLocationOptionValue(r.location) === e
        );
        setSelectedDropoffBranchForMap(matched ?? null);
        const nextDropoffState: MapPanelState = matched ? 3 : 2;
        setDropoffMapPanelState(nextDropoffState);
        dispatch(
          BOOKING_ACTIONS.setBranchMapSearch({
            branchSearchAddress,
            branchAddressCoords,
            branchDistanceMap,
            branchMapPanelState: mapPanelState,
            branchSelectedLocationCode:
              selectedBranchForMap?.location?.locationCode ?? null,
            dropoffMapPanelState: nextDropoffState,
            dropoffSelectedLocationCode:
              matched?.location?.locationCode ?? null,
          })
        );
      }
    },
    [
      showMapPanel,
      dropoffAddressCoords,
      dropoffRankedLocations,
      getLocationOptionValue,
      dispatch,
      branchSearchAddress,
      branchAddressCoords,
      branchDistanceMap,
      mapPanelState,
      selectedBranchForMap,
    ]
  );

  /** Reactive "Dropoff Location" selector, shown when home-return delivery is
   * active with an address. Same as handleDropoffChange, plus marking the
   * field as manually selected and clearing "drop at same location" (this
   * selector's own state, which the plain dropoff selector above never
   * touches since it only renders once that flag is already false). */
  const handleReactiveDropoffChange = useCallback(
    (e: string) => {
      setIsDropoffManuallySelected(true);
      setIsDroppingAtSameLocation(false);
      handleDropoffChange(e);
    },
    [handleDropoffChange]
  );

  /** Shared pickup onChange handler, used by every layout branch's
   * "Pick-up Location" selector (both the LocationSelectWithPanel and
   * AppInputSwitcher-fallback variants) so a future fix to this logic only
   * needs to land once instead of being hand-copied per branch. */
  const handlePickupChange = useCallback(
    (e: string) => {
      if (isDroppingAtSameLocation) {
        setReservationDestination(e);
      }
      setIsPickupManuallySelected(true);
      setPickupLoc(e);
      setPickupLocation(e);
      setStartDate(null);
      setEndDate(null);
      setStartDateError("");
      setEndDateError("");
      setDateError("");
      if (showMapPanel && branchAddressCoords) {
        const matched = e
          ? branchRankedLocations.find(
              (r) => getLocationOptionValue(r.location) === e
            )
          : undefined;
        setSelectedBranchForMap(matched ?? null);
        const nextState: MapPanelState = matched ? 3 : 2;
        setMapPanelState(nextState);
        dispatch(
          BOOKING_ACTIONS.setBranchMapSearch({
            branchSearchAddress,
            branchAddressCoords,
            branchDistanceMap,
            branchMapPanelState: nextState,
            branchSelectedLocationCode: matched?.location?.locationCode ?? null,
          })
        );
      }
    },
    [
      isDroppingAtSameLocation,
      showMapPanel,
      branchAddressCoords,
      branchRankedLocations,
      getLocationOptionValue,
      dispatch,
      branchSearchAddress,
      branchDistanceMap,
    ]
  );

  /** Shared onChange handler for the "drop at same location" checkbox, used
   * by every layout branch so a future fix to this logic only needs to land
   * once instead of being hand-copied per branch. */
  const handleDropAtSameLocationChange = useCallback(
    (e: string[]) => {
      setIsDroppingAtSameLocation(!isDroppingAtSameLocation);
      dropoffUserChangedRef.current = true;
      if (e.length) {
        setReservationDestination(pickupLocation);
        dispatch(BOOKING_ACTIONS.clearDropoffMapSearch());
        setDropoffRankedLocations([]);
        setDropoffAddressCoords(null);
        setSelectedDropoffBranchForMap(null);
        setDropoffMapPanelState(1);
      } else {
        setReservationDestination("");
        setDropupLocation("");
      }
    },
    [isDroppingAtSameLocation, pickupLocation, dispatch]
  );

  /** Shared BranchAddressSearch handlers for the pickup branch-map search,
   * used by every layout branch so a future fix to this logic only needs to
   * land once instead of being hand-copied per branch. */
  const handleBranchAddressSelected = useCallback(
    (
      address: string,
      coords: { lat: number; lng: number },
      ranked: BranchWithDistance[],
      distMap: Record<string, number>
    ) => {
      setBranchRankedLocations(ranked);
      setBranchSortedLocationList(ranked.map((r) => r.location));
      dispatch(
        BOOKING_ACTIONS.setBranchMapSearch({
          branchSearchAddress: address,
          branchAddressCoords: coords,
          branchDistanceMap: distMap,
          branchMapPanelState: 2,
          dropoffMapPanelState: 1,
          dropoffSelectedLocationCode: null,
        })
      );
      setMapPanelState(2);
      setSelectedBranchForMap(null);
      setSelectedDropoffBranchForMap(null);
      setDropoffMapPanelState(1);
    },
    [dispatch]
  );

  const handleBranchAddressCleared = useCallback(() => {
    setBranchRankedLocations([]);
    setBranchSortedLocationList([]);
    dispatch(BOOKING_ACTIONS.clearBranchMapSearch());
    setMapPanelState(1);
    setSelectedBranchForMap(null);
    setSelectedDropoffBranchForMap(null);
    setDropoffMapPanelState(1);
  }, [dispatch]);

  /** Shared BranchAddressSearch handlers for the dropoff branch-map search,
   * used by every layout branch so a future fix to this logic only needs to
   * land once instead of being hand-copied per branch. */
  const handleDropoffAddressSelected = useCallback(
    (
      address: string,
      coords: { lat: number; lng: number },
      ranked: BranchWithDistance[],
      distMap: Record<string, number>
    ) => {
      setDropoffRankedLocations(ranked);
      setDropoffAddressCoords(coords);
      setSelectedDropoffBranchForMap(null);
      setDropoffMapPanelState(2);
      dispatch(
        BOOKING_ACTIONS.setDropoffMapSearch({
          dropoffSearchAddress: address,
          dropoffAddressCoords: coords,
          dropoffDistanceMap: distMap,
          dropoffMapPanelState: 2,
          dropoffSelectedLocationCode: null,
        })
      );
    },
    [dispatch]
  );

  const handleDropoffAddressCleared = useCallback(() => {
    setDropoffRankedLocations([]);
    setDropoffAddressCoords(null);
    setSelectedDropoffBranchForMap(null);
    setDropoffMapPanelState(1);
    dispatch(BOOKING_ACTIONS.clearDropoffMapSearch());
  }, [dispatch]);

  /** Badge text shown under the delivery address field (nearest branch). */
  const closestDeliveryText: string | null = (() => {
    if (
      !homeDeliverySelected ||
      !deliveryCoordinates ||
      !deliverySortedLocations.length
    )
      return null;
    const nearest = deliverySortedLocations[0];
    const dist = locationDistanceMap[nearest.locationCode];
    if (dist == null) return null;
    const name = AppSettings?.showLocationWebDescription
      ? locationLang === "it" && nearest.publicWebDescription
        ? nearest.publicWebDescription
        : locationLang === "en" && nearest.publicWebDescriptionEN
        ? nearest.publicWebDescriptionEN
        : nearest.locationName
      : nearest.locationName;
    return `${name} — ${dist.toFixed(1)} KM`;
  })();

  /** Badge text shown under the return address field (nearest branch). */
  const closestReturnText: string | null = (() => {
    if (!homeReturnSelected || !returnCoordinates || !locationDetail.length)
      return null;
    const candidates = locationDetail
      .filter(hasValidGpsCoordinates)
      .map((loc) => ({
        loc,
        dist: getHaversineDistanceKm(
          returnCoordinates.lat,
          returnCoordinates.lng,
          loc.latitude,
          loc.longitude
        ),
      }))
      .sort((a, b) => a.dist - b.dist);
    if (!candidates.length) return null;
    const { loc: nearest, dist } = candidates[0];
    const name = AppSettings?.showLocationWebDescription
      ? locationLang === "it" && nearest.publicWebDescription
        ? nearest.publicWebDescription
        : locationLang === "en" && nearest.publicWebDescriptionEN
        ? nearest.publicWebDescriptionEN
        : nearest.locationName
      : nearest.locationName;
    return `${name} — ${dist.toFixed(1)} KM`;
  })();

  const deliveryOptionsWidget = AppSettings?.showHomeDeliveryAndReturnWidget ? (
    <DeliveryOptionsWidget
      homeDeliverySelected={homeDeliverySelected}
      homeReturnSelected={homeReturnSelected}
      homeDeliveryAddress={homeDeliveryAddress}
      homeReturnAddress={homeReturnAddress}
      onHomeDeliveryToggle={(val) => {
        setHomeDeliverySelected(val);
        if (!val) {
          setDeliveryCoordinates(null);
          setHomeDeliveryAddress("");
          setIsPickupManuallySelected(false);
          setPickupLocation("");
          setPickupLoc("");
          setStartDate(null);
          setEndDate(null);
        }
      }}
      onHomeReturnToggle={(val) => {
        setHomeReturnSelected(val);
        if (!val) {
          handleHomeReturnAddressChange("", null);
          setIsDropoffManuallySelected(false);
          setDropupLocation("");
          dropoffUserChangedRef.current = true;
          setReservationDestination("");
          setStartDate(null);
          setEndDate(null);
        }
      }}
      onHomeDeliveryAddressChange={(addr) => {
        setHomeDeliveryAddress(addr);
        if (!addr) setIsPickupManuallySelected(false);
      }}
      onHomeReturnAddressChange={(addr) => {
        setHomeReturnAddress(addr);
        if (!addr) setIsDropoffManuallySelected(false);
      }}
      onDeliveryCoordinatesChange={(coords) => {
        setDeliveryCoordinates(coords);
        setIsPickupManuallySelected(false);
      }}
      onReturnCoordinatesChange={(coords) => {
        setReturnCoordinates(coords);
        setIsDropoffManuallySelected(false);
      }}
      primaryColor={dynamicTheme?.palette?.["primary"]?.main ?? undefined}
      secondaryColor={
        dynamicTheme?.palette?.["secondaryColor"]?.main ?? undefined
      }
      closestDeliveryText={closestDeliveryText}
      closestReturnText={closestReturnText}
    />
  ) : null;

  const pickupDisplayValue: string = (() => {
    if (!pickupLocation) return "";

    // Home delivery — show label with distance (same pattern as before)
    if (homeDeliverySelected && isPickupManuallySelected) {
      const matched = effectivePickupLocations.find(
        (loc) => getLocationOptionValue(loc) === pickupLocation
      );
      return matched ? getPickupLocationLabel(matched) : pickupLocation;
    }

    if (showMapPanel && branchAddressCoords != null) {
      const matched = effectivePickupLocations.find(
        (loc) => getLocationOptionValue(loc) === pickupLocation
      );
      return matched ? getPickupLocationLabel(matched) : pickupLocation;
    }

    return pickupLocation;
  })();

  // ─── Drop-off display value (mirrors pickupDisplayValue for branch map) ───────
  const dropoffDisplayValue: string = (() => {
    if (!reservationDestination) return "";
    if (showMapPanel && dropoffAddressCoords != null) {
      const matched = effectiveDropoffLocations.find(
        (loc) => getLocationOptionValue(loc) === reservationDestination
      );
      return matched
        ? getDropoffLocationLabel(matched)
        : reservationDestination;
    }
    return reservationDestination;
  })();

  // ─────────────────────────────────────────────────────────────────────────────
  const handleHomeReturnAddressChange = (
    address: string,
    coords: { lat: number; lng: number } | null
  ) => {
    setHomeReturnAddress(address);
    setReturnCoordinates(coords);

    if (address && coords) {
      setIsDroppingAtSameLocation(false);

      // d) Same distance calculation/measuring and filtering/sorting logic as Pickup delivery locations
      const mapped = locationDetail
        .filter(hasValidGpsCoordinates)
        .map((loc) => ({
          loc,
          dist: getHaversineDistanceKm(
            coords.lat,
            coords.lng,
            loc.latitude,
            loc.longitude
          ),
        }))
        .sort((a, b) => a.dist - b.dist);

      const sortedLocs = mapped.map((m) => m.loc);
      const distMap: Record<string, number> = {};
      mapped.forEach((m) => {
        distMap[m.loc.locationCode] = m.dist;
      });

      setReturnSortedLocations(sortedLocs);
      setReturnLocationDistanceMap(distMap);
    } else {
      // Reset back to normal sorting and fallback states if cleared
      setReturnSortedLocations([]);
      setReturnLocationDistanceMap({});
    }
  };
  const getLocationByPickup = () =>
    locationDetail.find((e) => {
      const optionValue = getLocationOptionValue(e);
      return optionValue?.toLowerCase() === pickupLocation?.toLowerCase();
    });

  const getLocationByDropoff = () => {
    if (isDroppingAtSameLocation) return getLocationByPickup();
    const dropoffList = locationDropOffDetail?.length
      ? locationDropOffDetail
      : locationDetail;
    return dropoffList.find((e) => {
      const optionValue = getLocationOptionValue(e);
      return (
        optionValue?.toLowerCase() === reservationDestination?.toLowerCase()
      );
    });
  };

  const getDisabledDaysFromOpenings = (location: any): number[] => {
    const openDays = location?.openings?.map((o) => o.dayOfTheWeek) || [];
    const allDays = {
      0: true,
      1: true,
      2: true,
      3: true,
      4: true,
      5: true,
      6: true,
    };
    openDays.forEach((d) => {
      const jsDay = d === 7 ? 0 : d; // Convert Sunday(7) to 0 bcoz js day start with sunday but here we have started with monday
      delete allDays[jsDay];
    });
    return Object.keys(allDays).map(Number);
  };

  const isLocationFullOutOfHours = (location: any): boolean =>
    !!location?.isOutOfHours &&
    (location?.allowPickUpDropOffOutOfHours === true ||
      location?.allowPickUpDropOffOutOfHours === "true");

  const buildShouldDisableClosingDate = (
    location: any,
    isPickup: boolean
  ): ((date: Date) => boolean) => {
    if (!location) return () => false;

    const outOfHours =
      isLocationFullOutOfHours(location) ||
      (isPickup && (location as any).pickUpOutOfHours) ||
      (!isPickup &&
        (location.dropOffOutOfHours || location.onlyDropOffOutOfHours));

    const festivities: any[] =
      !outOfHours && Array.isArray((location as any).festivity)
        ? (location as any).festivity
        : [];

    const closings: LocationOpening[] =
      !outOfHours && Array.isArray(location.closing) ? location.closing : [];

    return (date: Date): boolean => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const dateStr = `${year}-${month}-${day}`;

      for (const fest of festivities) {
        const festDateStr =
          typeof fest?.date === "string" && fest.date.includes("T")
            ? fest.date.split("T")[0]
            : fest?.date;
        if (festDateStr === dateStr) return true;
      }

      for (const entry of closings) {
        if ((entry as any).ifClose === false) continue;

        if (entry.specificDay) {
          const specificDayStr = entry.specificDay.includes("T")
            ? entry.specificDay.split("T")[0]
            : entry.specificDay;

          if (specificDayStr === dateStr) {
            if (entry.isValidPeriod === "false") {
              return true;
            }
            if (!entry.validTo) return true;
            const validTo = new Date(entry.validTo);
            validTo.setHours(23, 59, 59, 999);
            if (date <= validTo) return true;
          }
        } else if (entry.dayOfTheWeek !== undefined) {
          const isFullPeriodClosure = Number(entry.dayOfTheWeek) === 0;
          if (isFullPeriodClosure) {
            //for the case when it is closed for a certain period and not any weekday is selected
            if (entry.validTo) {
              const fromStr = entry.validFrom
                ? entry.validFrom.split("T")[0]
                : "0000-01-01";
              const toStr = entry.validTo.split("T")[0];
              if (dateStr >= fromStr && dateStr <= toStr) return true;
            }
          } else {
            const jsDay = entry.dayOfTheWeek % 7;
            if (date.getDay() === jsDay && entry.validTo) {
              const validTo = new Date(entry.validTo);
              validTo.setHours(23, 59, 59, 999);
              const validFrom = entry.validFrom
                ? new Date(entry.validFrom)
                : new Date(0);
              if (date >= validFrom && date <= validTo) return true;
            }
          }
        }
      }
      return false;
    };
  };

  // Disabled days for the pickup (start) date picker
  const disableDate: number[] =
    pickupLocation && locationDetail.length
      ? (() => {
          const location = getLocationByPickup();
          if (
            isLocationFullOutOfHours(location) ||
            (location as any)?.pickUpOutOfHours
          ) {
            return [];
          }
          return getDisabledDaysFromOpenings(location);
        })()
      : [];

  // reservationDestination may be empty (e.g. redux restore without dropOffLocationDetails)
  const dropoffGuard = isDroppingAtSameLocation
    ? pickupLocation
    : reservationDestination;

  // Disabled days for the dropoff (end) date picker
  const disableDropoffDate: number[] =
    dropoffGuard && locationDetail.length
      ? (() => {
          const location = getLocationByDropoff();
          if (
            isLocationFullOutOfHours(location) ||
            location?.dropOffOutOfHours ||
            location?.onlyDropOffOutOfHours
          ) {
            return [];
          }
          return getDisabledDaysFromOpenings(location);
        })()
      : [];

  const pickupShouldDisableDate = (() => {
    if (!pickupLocation || !locationDetail.length) return () => false;
    return buildShouldDisableClosingDate(getLocationByPickup(), true);
  })();

  const dropoffShouldDisableDate = (() => {
    if (!dropoffGuard || !locationDetail.length) return () => false;
    const loc = getLocationByDropoff();
    return buildShouldDisableClosingDate(loc, false);
  })();

  // ─── Proximity sorting for home delivery (Existing) ──────────────────────────
  useEffect(() => {
    if (
      !homeDeliverySelected ||
      !deliveryCoordinates ||
      !locationDetail.length
    ) {
      setDeliverySortedLocations([]);
      setLocationDistanceMap({});
      return;
    }
    const mapped = locationDetail
      .filter(hasValidGpsCoordinates)
      .map((loc) => ({
        loc,
        dist: getHaversineDistanceKm(
          deliveryCoordinates.lat,
          deliveryCoordinates.lng,
          loc.latitude,
          loc.longitude
        ),
      }))
      .sort((a, b) => a.dist - b.dist);

    setDeliverySortedLocations(mapped.map((m) => m.loc));
    const distMap: Record<string, number> = {};
    mapped.forEach((m) => {
      distMap[m.loc.locationCode] = m.dist;
    });
    setLocationDistanceMap(distMap);
  }, [homeDeliverySelected, deliveryCoordinates, locationDetail]);

  // ─── NEW: Proximity sorting for home return drop-off ────────────────────────
  useEffect(() => {
    if (!homeReturnSelected || !returnCoordinates || !locationDetail.length) {
      setReturnSortedLocations([]);
      setReturnLocationDistanceMap({});
      return;
    }
    const mapped = locationDetail
      .filter(hasValidGpsCoordinates)
      .map((loc) => ({
        loc,
        dist: getHaversineDistanceKm(
          returnCoordinates.lat,
          returnCoordinates.lng,
          loc.latitude,
          loc.longitude
        ),
      }))
      .sort((a, b) => a.dist - b.dist);

    setReturnSortedLocations(mapped.map((m) => m.loc));
    const distMap: Record<string, number> = {};
    mapped.forEach((m) => {
      distMap[m.loc.locationCode] = m.dist;
    });
    setReturnLocationDistanceMap(distMap);
  }, [homeReturnSelected, returnCoordinates, locationDetail]);

  const mainClassName = `h-full ${
    companyCode ? "" : "flex justify-center"
  } box-border ${className}`;

  return (
    <ThemeProvider theme={dynamicTheme}>
      {isModalLoading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            backdropFilter: "blur(1px)",
            WebkitBackdropFilter: "blur(1px)",
          }}
        >
          <CircularProgress
            style={{ color: dynamicTheme?.palette["primary"]?.main || "white" }}
          />
        </div>
      )}

      {isInitializing && (
        <div ref={skeletonRef} className={mainClassName}>
          <BookingFormSkeleton
            isEnableHorizontalView={isEnableHorizontalView}
          />
        </div>
      )}
      <main
        className={mainClassName}
        style={{ display: isInitializing ? "none" : undefined }}
      >
        <form
          ref={bookingFormRef}
          onSubmit={async (e) => {
            // onSubmit={(e) => {
            e.preventDefault();
            const focusDateGroup = (
              groupRef: React.RefObject<HTMLDivElement>
            ) => {
              const input = groupRef.current?.querySelector("input");
              input?.focus();
              input?.scrollIntoView({ block: "center", behavior: "smooth" });
            };
            if (AppSettings?.showSeparatePickDropDate) {
              if (!reservationStartDate) {
                const msg = `${pickUpDate} ${translate("is required")}`;
                setStartDateError(msg);
                setEndDateError("");
                // Fallback for old-UI layouts that only render the shared line.
                setDateError(msg);
                focusDateGroup(pickupDateGroupRef);
                return;
              }
              setStartDateError("");
              if (!reservationEndDate) {
                const msg = `${dropOffDate} ${translate("is required")}`;
                setEndDateError(msg);
                setDateError(msg);
                focusDateGroup(dropoffDateGroupRef);
                return;
              }
              setEndDateError("");
              setDateError("");
            } else if (!reservationStartDate || !reservationEndDate) {
              // Combined single "Reservation date" field.
              setDateError(
                `${translate("reservation dates")} ${translate("is required")}`
              );
              return;
            }
            if (agreementCoupon.trim() && validateAgreementCoupon) {
              const isCouponValid = await validateDiscountCode(agreementCoupon);
              if (!isCouponValid) {
                return;
              }
            }
            if (
              AppSettings?.minLeadTimeInHours &&
              AppSettings?.minLeadTimeInHours > 0
            ) {
              const start_date_time =
                reservationStartDate?.getTime() || Date.now();
              const parsedStartDate = new Date(start_date_time);
              const now = new Date();
              const nowWithLead = new Date(now);
              nowWithLead.setHours(
                now.getHours() + AppSettings.minLeadTimeInHours
              );
              const currentLang = locationLang || "it";
              const formattedDateTime = new Intl.DateTimeFormat(currentLang, {
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false, // for 24-hour format
              });
              const parts = formattedDateTime.formatToParts(nowWithLead);
              const getVal = (type: string) =>
                parts.find((p) => p.type === type)?.value;

              const time = `${getVal("hour")}:${getVal("minute")}`;
              const day = getVal("day");
              const month = getVal("month");
              const year = getVal("year");
              if (parsedStartDate < nowWithLead) {
                // 4. Handle grammar connectors per language
                let customFormattedDate = "";
                switch (currentLang) {
                  case "en":
                    customFormattedDate = `${month} ${day}, ${year} at ${time}`; // e.g., May 5, 2026 at 17:00
                    break;
                  case "es":
                    customFormattedDate = `${time} del ${day} de ${month} de ${year}`; // e.g., 17:00 del 5 de mayo de 2026
                    break;
                  case "fr":
                    customFormattedDate = `${time} le ${day} ${month} ${year}`; // e.g., 17:00 le 5 mai 2026
                    break;
                  case "it":
                  default:
                    customFormattedDate = `${time} del ${day} ${month} ${year}`; // e.g., 17:00 del 5 maggio 2026
                    break;
                }
                setShowModal(true);
                const modalMessage = String(leadTimeAlertMsg).replace(
                  "{timeValue}",
                  customFormattedDate
                );
                setModalMessage(modalMessage);
                return;
              }
            }
            let selectedRS;
            if (reservationSourceEnabled) {
              selectedRS = reservationSourceForUser?.find(
                (e) =>
                  e.description === reservationPlatform ||
                  e["Ragione Sociale textRS"] === reservationPlatform ||
                  e["Ragione Sociale"] === reservationPlatform
              );
            } else {
              selectedRS = reservationSourceOptions?.find(
                (e) =>
                  e["Ragione Sociale textRS"] === reservationPlatform ||
                  e["Ragione Sociale"] === reservationPlatform
              );
            }
            const selectedPickupLocation = effectivePickupLocations.filter(
              (e) => getLocationOptionValue(e) === pickupLocation
            )[0];
            const selectedDropoffLocation = (
              locationDropOffDetail.length
                ? locationDropOffDetail
                : locationDetail
            ).filter(
              (e) => getLocationOptionValue(e) === reservationDestination
            )[0];
            const bookingDetailsForLinkDirection: BookingDetails = {
              dropOffDateAndTime: reservationEndDate?.getTime() || Date.now(),
              dropOffLocationDetails: {
                name: reservationDestination,
                code: selectedDropoffLocation?.locationCode,
                locationCity: selectedDropoffLocation?.locationCity,
                locationAddress: selectedDropoffLocation?.locationAddress,
                locationNumber: selectedDropoffLocation?.locationNumber,
                zipCode: selectedDropoffLocation?.zipCode,
                publicWebDescription:
                  selectedDropoffLocation?.publicWebDescription,
                publicWebDescriptionEN:
                  selectedDropoffLocation?.publicWebDescriptionEN,
                reservationSourcePayNow: selectedRS?.payNow ?? false,
                reservationSourcePayLater: selectedRS?.payLater ?? false,
              },
              dropOffLocationName: reservationDestination,
              pickUpLocationDetails: {
                name: pickupLocation,
                code: selectedPickupLocation?.locationCode,
                locationCity: selectedPickupLocation?.locationCity,
                locationAddress: selectedPickupLocation?.locationAddress,
                locationNumber: selectedPickupLocation?.locationNumber,
                zipCode: selectedPickupLocation?.zipCode,
                publicWebDescription:
                  selectedPickupLocation?.publicWebDescription,
                publicWebDescriptionEN:
                  selectedPickupLocation?.publicWebDescriptionEN,
                reservationSourcePayNow: selectedRS?.payNow ?? false,
                reservationSourcePayLater: selectedRS?.payLater ?? false,
                isAirport: selectedPickupLocation?.isAirport,
              },
              affiliateDetails: {
                name: selectedAffiliateName,
                code: selectedAffiliate || "",
              },
              pickUpLocationName: pickupLocation,
              pickUpDateAndTime: reservationStartDate?.getTime() || Date.now(),
              reservationSource: {
                name: reservationPlatform,
                code:
                  selectedRS?.Codice ||
                  selectedRS?.code ||
                  ((isInvalidScriptChannelResSource && scriptChannel) ??
                    scriptChannel) ||
                  "",
                reservationSourcePayNow: selectedRS?.payNow ?? false,
                reservationSourcePayLater: selectedRS?.payLater ?? false,
                locationCity: "",
                locationAddress: "",
                locationNumber: "",
                zipCode: "",
                publicWebDescription: "",
                publicWebDescriptionEN: "",
              },
              agreementCoupon: agreementCoupon.trim(),
              discountValueWithoutVat: discountValueWithoutVat,
              macroGroupName: selectedMacroGroupWebCode,
              showPics: true,
              showOptionalImage: true,
              showVehicleParameter: true,
              showVehicleExtraImage: true,
              showBookingDiscount: true,
              isYoungDriverAge: isYoungDriverAge,
              isYoungDriver2Age: youngDriver2,
              isSeniorDriverAge: isSeniorDriverAge,
              noFeeAge: isNoFeeAge,
              isAgeLimitSelected: isAgeLimitSelected,
              payLaterFeeAmt: "",
              isPayLaterFee: false,
              dropAtSameLocation: isDroppingAtSameLocation,
              reservationSourcePayNow: selectedRS?.payNow ?? false,
              reservationSourcePayLater: selectedRS?.payLater ?? false,
              youngDriverAgeValue: ageCriteriaData?.["youngDriver"],
              youngDriver2AgeValue: ageCriteriaData?.["youngDriver2"],
              seniorDriverAgeValue: ageCriteriaData?.["seniorDriver"],
              noAgeDriverValue: ageCriteriaData?.["noAgeDriver"],
              scriptChannel:
                queryParams.get("scriptChannel") &&
                isScriptChannelResSource &&
                !userId
                  ? queryParams.get("scriptChannel")
                  : "",
              isInvalidScriptChannelResSource: isInvalidScriptChannelResSource,
              scriptAffiliate: queryParams.get("scriptAffiliate")
                ? queryParams.get("scriptAffiliate")
                : "",
              scriptVoucherCode: queryParams.get("scriptVoucherCode")
                ? queryParams.get("scriptVoucherCode")
                : "",
              reservedAreaPayNow: reservedAreaPayNow || false,
              // ─── Home Delivery / Home Return ──────────────────────────────
              homeDeliverySelected,
              homeReturnSelected,
              homeDeliveryAddress,
              homeReturnAddress,
              deliveryCoordinates,
              returnCoordinates,
              homeDeliveryAddressKm:
                homeDeliverySelected && selectedPickupLocation
                  ? locationDistanceMap[selectedPickupLocation.locationCode] !=
                    null
                    ? parseFloat(
                        locationDistanceMap[
                          selectedPickupLocation.locationCode
                        ].toFixed(2)
                      )
                    : undefined
                  : undefined,
              homeReturnAddressKm:
                homeReturnSelected && selectedDropoffLocation
                  ? returnLocationDistanceMap[
                      selectedDropoffLocation.locationCode
                    ] != null
                    ? parseFloat(
                        returnLocationDistanceMap[
                          selectedDropoffLocation.locationCode
                        ].toFixed(2)
                      )
                    : undefined
                  : undefined,
            };
            trackSearch({
              pickUpLocationCode:
                bookingDetailsForLinkDirection.pickUpLocationDetails?.code,
              pickUpLocationName:
                bookingDetailsForLinkDirection.pickUpLocationName,
              dropOffLocationCode:
                bookingDetailsForLinkDirection.dropOffLocationDetails?.code,
              dropOffLocationName:
                bookingDetailsForLinkDirection.dropOffLocationName,
              pickUpDateTime: bookingDetailsForLinkDirection.pickUpDateAndTime,
              dropOffDateTime:
                bookingDetailsForLinkDirection.dropOffDateAndTime,
              coupon: bookingDetailsForLinkDirection.agreementCoupon,
            });
            onSubmit(bookingDetailsForLinkDirection);
          }}
        >
          {isEnableHorizontalView === false ? (
            AppSettings?.isGretaBookingNewUi ? (
              <>
                {AppSettings?.pageTitle && (
                  <>
                    <span className="text-white font-bold text-xl">
                      {t("Book Online")}
                    </span>
                    <span className="text-white font-bold text-xl">-</span>
                    <span className="text-white font-bold text-xl">
                      {pageTitleLabel}
                    </span>
                    <br />
                    <br />
                  </>
                )}
                <div
                  className={
                    showMapPanel
                      ? "flex flex-col md:flex-row w-full md:w-[72vw] md:max-w-[1120px] md:items-stretch gap-4"
                      : "w-full"
                  }
                >
                  <div
                    className={
                      showMapPanel
                        ? "w-full md:w-1/2 min-w-0 flex flex-col"
                        : "w-full"
                    }
                  >
                    <AppCard
                      className="booking-new-ui flex flex-col bg-white w-full box-border p-6 gap-4"
                      style={{
                        borderRadius: "20px",
                      }}
                    >
                      {/* Macro Group Icon Tabs — inside card at top */}
                      {Array.isArray(macroGroupOptions) &&
                        macroGroupOptions.length > 0 && (
                          <div className="flex flex-row gap-2">
                            {macroGroupOptions.map((option, index) => {
                              const isActive =
                                selectedMacroGroup === option.label;
                              const labelLower = option.label.toLowerCase();
                              let tabIcon = (
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="22"
                                  height="22"
                                  viewBox="0 0 64 32"
                                  fill="currentColor"
                                >
                                  {/* Car body */}
                                  <rect
                                    x="2"
                                    y="16"
                                    width="60"
                                    height="10"
                                    rx="3"
                                  />
                                  {/* Cabin roof */}
                                  <path d="M14 16 L22 5 L44 5 L52 16 Z" />
                                  {/* Front wheel */}
                                  <circle
                                    cx="48"
                                    cy="26"
                                    r="6"
                                    fill="currentColor"
                                  />
                                  <circle
                                    cx="48"
                                    cy="26"
                                    r="2.5"
                                    fill="white"
                                  />
                                  {/* Rear wheel */}
                                  <circle
                                    cx="16"
                                    cy="26"
                                    r="6"
                                    fill="currentColor"
                                  />
                                  <circle
                                    cx="16"
                                    cy="26"
                                    r="2.5"
                                    fill="white"
                                  />
                                </svg>
                              );
                              if (
                                labelLower.includes("furgon") ||
                                labelLower.includes("van")
                              ) {
                                tabIcon = (
                                  <LocalShippingIcon fontSize="small" />
                                );
                              } else if (
                                labelLower.includes("scooter") ||
                                labelLower.includes("moto")
                              ) {
                                tabIcon = <TwoWheelerIcon fontSize="small" />;
                              }
                              return (
                                <button
                                  key={option.WebCode ?? `macro-group-${index}`}
                                  type="button"
                                  onClick={() => {
                                    if (selectedMacroGroup === option.label) {
                                      setSelectedMacroGroup("");
                                      setSelectedMacroGroupWebCode("");
                                    } else {
                                      setSelectedMacroGroup(option.label);
                                      setSelectedMacroGroupWebCode(
                                        option.WebCode
                                      );
                                    }
                                  }}
                                  className={`macro-group-pill${
                                    isActive ? " is-active" : ""
                                  }`}
                                >
                                  {tabIcon}
                                  <span className="hidden sm:inline">
                                    {option.label.charAt(0).toUpperCase() +
                                      option.label.slice(1).toLowerCase()}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      {deliveryOptionsWidget}
                      {showMapPanel && (
                        <BranchAddressSearch
                          locations={locationDetail}
                          initialAddress={branchSearchAddress}
                          primaryColor={
                            dynamicTheme?.palette?.["primary"]?.main ??
                            undefined
                          }
                          secondaryColor={
                            dynamicTheme?.palette?.["secondaryColor"]?.main ??
                            undefined
                          }
                          onAddressSelected={handleBranchAddressSelected}
                          onAddressCleared={handleBranchAddressCleared}
                        />
                      )}
                      {showMapPanel &&
                        !isDroppingAtSameLocation &&
                        !isHomeReturnWithAddressActive && (
                          <BranchAddressSearch
                            label={translate("Search nearby drop-off branches")}
                            locations={
                              locationDropOffDetail.length
                                ? locationDropOffDetail
                                : locationDetail
                            }
                            initialAddress={dropoffSearchAddress}
                            primaryColor={
                              dynamicTheme?.palette?.["secondaryColor"]?.main ??
                              undefined
                            }
                            secondaryColor={
                              dynamicTheme?.palette?.["secondaryColor"]?.main ??
                              undefined
                            }
                            onAddressSelected={handleDropoffAddressSelected}
                            onAddressCleared={handleDropoffAddressCleared}
                          />
                        )}
                      <div
                        className={
                          showMapPanel
                            ? "flex flex-col gap-3 w-full"
                            : "flex lg:flex-row gap-3 flex-col w-full lg:items-end"
                        }
                      >
                        {isAffiliateEnable && !scriptAffiliate && (
                          <AppInputSwitcher
                            required={true}
                            className={
                              "md:grow flex md:flex-row gap-2 flex-col min-w-[0rem] md:min-w-[19rem]"
                            }
                            value={selectedAffiliateName}
                            label={translate("Choose the partner to rent with")}
                            key={"affiliate"}
                            options={affiliateOptions}
                            type="select"
                            variant="with-search"
                            disabled={true}
                            onchange={(selectedName) => {
                              const selectedAffiliate = affiliateDetail.find(
                                (e) => e[1] === selectedName
                              );
                              if (selectedAffiliate) {
                                const id = selectedAffiliate[0];
                                const name = selectedAffiliate[1];
                                setSelectedAffiliateName(name);
                                setSelectedAffiliate(id);
                                setPickupLocation(null);
                                setStartDate(null);
                                setEndDate(null);
                              }
                            }}
                          />
                        )}
                        {/* Location fields wrapper — shares space equally between pickup and dropoff */}
                        <div
                          className={`flex gap-3 min-w-[0rem] lg:flex-1 lg:grow lg:min-w-[26rem] w-full ${
                            !isDroppingAtSameLocation
                              ? "flex-col sm:flex-row"
                              : "flex-row"
                          }`}
                        >
                          {/* Pickup location */}
                          <div className="flex flex-col gap-1 flex-1 min-w-0">
                            <span
                              className="text-xs font-semibold"
                              style={{ color: "#000000" }}
                            >
                              {isDroppingAtSameLocation
                                ? translate("Pickup and return")
                                : translate("pick up location")}
                            </span>
                            {showLocationPanel ? (
                              <LocationSelectWithPanel
                                key={"pickUpLocation"}
                                required={true}
                                className={"w-full"}
                                value={
                                  homeDeliverySelected &&
                                  !isPickupManuallySelected
                                    ? ""
                                    : pickupLocation
                                }
                                label=""
                                placeholder={
                                  isDroppingAtSameLocation
                                    ? translate("Pickup and return")
                                    : translate("pick up location")
                                }
                                options={effectivePickupLocations || []}
                                disableSoftKeyboard={
                                  !!(
                                    AppSettings?.notOpenKeyboardForLocation &&
                                    smallScreen
                                  )
                                }
                                // Reuses BookingForm's own getLocationOptionValue
                                // helper (line ~1935) — same string shape
                                // getLocationByPickup/Dropoff + calendar
                                // matchers expect (CLAUDE.md §14 landmine).
                                getOptionValue={getLocationOptionValue}
                                // getPickupLocationLabel is showMapPanel-agnostic
                                // so the home-delivery km distance badge shows
                                // even when showMapPanel is off for this tenant.
                                getOptionLabel={getPickupLocationLabel}
                                getSortValue={getPickupDistanceSortValue}
                                anchorDate={reservationStartDate}
                                recentKey={"pickup"}
                                onChange={handlePickupChange}
                              />
                            ) : (
                              /* BOOKING-984 rollback — pre-BOOKING-984 UI restored verbatim. */
                              <AppInputSwitcher
                                required={true}
                                className={"w-full"}
                                value={
                                  homeDeliverySelected &&
                                  !isPickupManuallySelected
                                    ? ""
                                    : pickupDisplayValue
                                }
                                label=""
                                placeholder={
                                  isDroppingAtSameLocation
                                    ? translate("Pickup and return")
                                    : translate("pick up location")
                                }
                                key={"pickUpLocation"}
                                options={effectivePickupLocations?.map(
                                  (e: Location) => {
                                    let icon = (
                                      <LocationCityIcon color="primary" />
                                    );
                                    if (e.isAirport) {
                                      icon = (
                                        <FlightTakeoffIcon htmlColor="blue" />
                                      );
                                    } else if (e.isRailway) {
                                      icon = <TrainIcon htmlColor="green" />;
                                    }
                                    return {
                                      label: getPickupLocationLabel(e),
                                      value: getLocationOptionValue(e),
                                      icon,
                                    };
                                  }
                                )}
                                type="select"
                                variant={
                                  AppSettings?.notOpenKeyboardForLocation &&
                                  smallScreen
                                    ? "with-search-label"
                                    : "with-search"
                                }
                                onchange={handlePickupChange}
                              />
                            )}
                          </div>

                          {/* Drop-off location — reactive "Dropoff Location" selector when home-return delivery is active with an address, same as the non-Greta UI */}
                          {isHomeReturnWithAddressActive ? (
                            <div className="flex flex-col gap-1 flex-1 min-w-0">
                              <span
                                className="text-xs font-semibold"
                                style={{ color: "#000000" }}
                              >
                                {translate("Dropoff Location")}
                              </span>
                              {showLocationPanel ? (
                                <LocationSelectWithPanel
                                  key={"dropoff_return_reactive"}
                                  required={true}
                                  className="w-full"
                                  value={
                                    !isDropoffManuallySelected
                                      ? ""
                                      : reservationDestination
                                  }
                                  placeholder={translate("Dropoff Location")}
                                  options={effectiveDropoffLocations || []}
                                  getOptionValue={getLocationOptionValue}
                                  getOptionLabel={getReturnOptionLabel}
                                  getSortValue={getReturnDistanceSortValue}
                                  anchorDate={reservationStartDate}
                                  recentKey={"dropoff"}
                                  onChange={handleReactiveDropoffChange}
                                />
                              ) : (
                                <AppInputSwitcher
                                  required={true}
                                  className="w-full"
                                  value={
                                    !isDropoffManuallySelected
                                      ? ""
                                      : reservationDestination
                                  }
                                  placeholder={translate("Dropoff Location")}
                                  key={"dropoff_return_reactive"}
                                  options={effectiveDropoffLocations.map(
                                    (e) => {
                                      let icon = (
                                        <LocationCityIcon color="primary" />
                                      );
                                      if (e.isAirport) {
                                        icon = (
                                          <FlightTakeoffIcon htmlColor="blue" />
                                        );
                                      } else if (e.isRailway) {
                                        icon = <TrainIcon htmlColor="green" />;
                                      }
                                      return {
                                        label: getReturnOptionLabel(e),
                                        value: getLocationOptionValue(e),
                                        icon,
                                      };
                                    }
                                  )}
                                  type="select"
                                  variant="with-search"
                                  onchange={handleReactiveDropoffChange}
                                />
                              )}
                            </div>
                          ) : (
                            !isDroppingAtSameLocation && (
                              <div className="flex flex-col gap-1 flex-1 min-w-0">
                                <span
                                  className="text-xs font-semibold"
                                  style={{ color: "#000000" }}
                                >
                                  {translate("drop off location")}
                                </span>
                                {showLocationPanel ? (
                                  <LocationSelectWithPanel
                                    key={"dropOffLocation"}
                                    required={true}
                                    className="w-full"
                                    value={reservationDestination}
                                    placeholder={translate("drop off location")}
                                    options={effectiveDropoffLocations || []}
                                    // Preserves the exact option-value string
                                    getOptionValue={getLocationOptionValue}
                                    getOptionLabel={getDropoffLocationLabel}
                                    getSortValue={getDropoffDistanceSortValue}
                                    anchorDate={reservationStartDate}
                                    recentKey={"dropoff"}
                                    onChange={handleDropoffChange}
                                  />
                                ) : (
                                  /* BOOKING-984 rollback — pre-BOOKING-984 UI restored verbatim. */
                                  <AppInputSwitcher
                                    required={true}
                                    className="w-full"
                                    value={
                                      showMapPanel
                                        ? dropoffDisplayValue
                                        : reservationDestination
                                    }
                                    placeholder={translate("drop off location")}
                                    label=""
                                    key={"dropOffLocation"}
                                    options={effectiveDropoffLocations.map(
                                      (e) => {
                                        let icon = (
                                          <LocationCityIcon color="primary" />
                                        );
                                        if (e.isAirport) {
                                          icon = (
                                            <FlightTakeoffIcon htmlColor="blue" />
                                          );
                                        } else if (e.isRailway) {
                                          icon = (
                                            <TrainIcon htmlColor="green" />
                                          );
                                        }
                                        return {
                                          label: getDropoffLocationLabel(e),
                                          value: getLocationOptionValue(e),
                                          icon,
                                        };
                                      }
                                    )}
                                    type="select"
                                    variant="with-search"
                                    onchange={handleDropoffChange}
                                  />
                                )}
                              </div>
                            )
                          )}
                        </div>
                        {AppSettings?.showSeparatePickDropDate ? (
                          <>
                            {/* Pickup date + time group */}
                            <div
                              ref={pickupDateGroupRef}
                              className="flex flex-col gap-1 w-full lg:w-auto relative"
                            >
                              <span
                                className="text-xs font-semibold"
                                style={{ color: "#000000" }}
                              >
                                {pickUpDate}
                                <span className="text-red-600"> *</span>
                              </span>
                              <div className="date-time-group flex flex-row items-stretch border border-gray-300 rounded-[14px] bg-white w-full">
                                <DatePickerComponentNew
                                  sx={{
                                    svg: { color: "primary" },
                                    input: { color: "primary" },
                                    label: { color: "primary" },
                                    backgroundColor: "primary",
                                  }}
                                  required={true}
                                  disableOnDay={disableDate}
                                  shouldDisableDateFn={pickupShouldDisableDate}
                                  iconPosition="start"
                                  inputFormat="DD MMM"
                                  inputPlaceholder={translate("Date")}
                                  className="w-[8rem]"
                                  label=""
                                  value={reservationStartDate}
                                  minDate={dayjs().add(
                                    minRentalAllowedDay,
                                    "day"
                                  )}
                                  maxDate={dayjs().add(365, "day")}
                                  onChange={(selectedStartDate) => {
                                    if (selectedStartDate) {
                                      setStartDateError("");
                                      const selectedLocation =
                                        locationDetail.find((e) => {
                                          const optionValue =
                                            getLocationOptionValue(e);
                                          return (
                                            optionValue?.toLowerCase() ===
                                            pickupLocation?.toLowerCase()
                                          );
                                        });
                                      if (
                                        selectedLocation &&
                                        selectedLocation.onlyDropOffOutOfHours &&
                                        Array.isArray(selectedLocation.closing)
                                      ) {
                                        const startDay = getDay(
                                          selectedStartDate.toDate()
                                        );

                                        // Check if the selected date falls in any closed period
                                        const isDateClosed =
                                          selectedLocation.closing.some(
                                            (entry) => {
                                              if (
                                                entry.dayOfTheWeek % 7 ===
                                                startDay
                                              ) {
                                                if (
                                                  entry.validFrom &&
                                                  entry.validTo
                                                ) {
                                                  const validFrom = new Date(
                                                    entry.validFrom
                                                  );
                                                  const validTo = new Date(
                                                    entry.validTo
                                                  );
                                                  const selectedDate =
                                                    selectedStartDate.toDate();

                                                  return (
                                                    selectedDate >= validFrom &&
                                                    selectedDate <= validTo
                                                  );
                                                }
                                                return true;
                                              }
                                              return false;
                                            }
                                          );

                                        if (isDateClosed) {
                                          setStartDateError(
                                            translate(
                                              "The date is not available for booking."
                                            )
                                          );
                                          setStartDate(null);
                                          return;
                                        }
                                      }

                                      if (selectedLocation) {
                                        const openingObj =
                                          selectedLocation.openings?.find(
                                            (e) =>
                                              getDay(
                                                selectedStartDate.toDate()
                                              ) ===
                                              e.dayOfTheWeek % 7
                                          );

                                        const opening =
                                          selectedLocation.isOutOfHours ||
                                          selectedLocation.pickUpOutOfHours
                                            ? openingObj?.LocationStartTime
                                            : openingObj?.startTime;

                                        setStartDate(
                                          setTime(
                                            opening || "",
                                            selectedStartDate.toDate()
                                          )
                                        );
                                      }
                                    } else {
                                      setStartDate(null);
                                    }
                                  }}
                                />
                                <div className="w-px bg-gray-200 self-stretch" />
                                <AppInputSwitcher
                                  required={false}
                                  disabled={!reservationStartDate}
                                  className="w-[8rem]"
                                  label=""
                                  placeholder={translate("Time")}
                                  key={"pickUpTime"}
                                  enableTimesInRange={getEnabledTimeSlots(
                                    locationDetail,
                                    pickupLocation,
                                    reservationStartDate
                                  )}
                                  value={reservationStartDate}
                                  options={[]}
                                  type="time"
                                  variant="slot"
                                  onchange={(e) => {
                                    setStartDate(e);
                                  }}
                                />
                              </div>
                              {startDateError && (
                                <p className="text-red-500 text-xs absolute top-full left-0 mt-1 whitespace-nowrap">
                                  {startDateError}
                                </p>
                              )}
                            </div>
                            {/* Dropoff date + time group */}
                            <div
                              ref={dropoffDateGroupRef}
                              className="flex flex-col gap-1 w-full lg:w-auto relative"
                            >
                              <span
                                className="text-xs font-semibold"
                                style={{ color: "#000000" }}
                              >
                                {dropOffDate}
                                <span className="text-red-600"> *</span>
                              </span>
                              <div className="date-time-group flex flex-row items-stretch border border-gray-300 rounded-[14px] bg-white w-full">
                                <DatePickerComponentNew
                                  sx={{
                                    svg: { color: "primary" },
                                    input: { color: "primary" },
                                    label: { color: "primary" },
                                    backgroundColor: "primary",
                                  }}
                                  className="w-[8rem]"
                                  required={true}
                                  iconPosition="start"
                                  inputFormat="DD MMM"
                                  inputPlaceholder={translate("Date")}
                                  disableOnDay={disableDropoffDate}
                                  shouldDisableDateFn={dropoffShouldDisableDate}
                                  label=""
                                  value={reservationEndDate}
                                  minDate={
                                    reservationStartDate
                                      ? dayjs(reservationStartDate)
                                      : dayjs().add(minRentalAllowedDay, "day")
                                  }
                                  maxDate={
                                    reservationStartDate
                                      ? dayjs(reservationStartDate).add(
                                          maxRentalAllowedDay,
                                          "day"
                                        )
                                      : dayjs().add(365, "day")
                                  }
                                  onChange={(selectedEndDate) => {
                                    if (selectedEndDate) {
                                      setEndDateError("");
                                      const selectedLocation =
                                        locationDetail.find((e) => {
                                          const optionValue =
                                            getLocationOptionValue(e);
                                          return (
                                            optionValue?.toLowerCase() ===
                                            reservationDestination?.toLowerCase()
                                          );
                                        });

                                      const opening =
                                        selectedLocation?.openings?.find(
                                          (e) =>
                                            getDay(selectedEndDate.toDate()) ===
                                            e.dayOfTheWeek % 7
                                        )?.startTime;
                                      setEndDate(
                                        setTime(
                                          opening || "",
                                          selectedEndDate.toDate()
                                        )
                                      );
                                    } else {
                                      setEndDate(null);
                                    }
                                  }}
                                />
                                <div className="w-px bg-gray-200 self-stretch" />
                                <AppInputSwitcher
                                  enableTimesInRange={getEnabledDropoffTimeSlots(
                                    locationDetail,
                                    reservationDestination,
                                    reservationEndDate
                                  )}
                                  disabled={!reservationEndDate}
                                  required={false}
                                  className="w-[8rem]"
                                  label=""
                                  placeholder={translate("Time")}
                                  key={"dropOffTime"}
                                  options={[]}
                                  value={reservationEndDate}
                                  type="time"
                                  variant="slot"
                                  onchange={(e) => {
                                    setEndDate(e);
                                  }}
                                />
                              </div>
                              {endDateError && (
                                <p className="text-red-500 text-xs absolute top-full left-0 mt-1 whitespace-nowrap">
                                  {endDateError}
                                </p>
                              )}
                            </div>
                          </>
                        ) : (
                          <>
                            <AppInputSwitcher
                              disableOnDay={disableDate}
                              required={true}
                              className="w-full grow md:min-w-72"
                              label={translate("reservation dates")}
                              key={"reservationDates"}
                              options={[]}
                              startDate={reservationStartDate}
                              endDate={reservationEndDate}
                              minDate={addDays(new Date(), minRentalAllowedDay)}
                              maxDate={
                                reservationStartDate
                                  ? addDays(
                                      new Date(
                                        reservationStartDate?.toString()
                                      ),
                                      maxRentalAllowedDay
                                    )
                                  : addDays(new Date(), 365)
                              }
                              type="date"
                              variant="range"
                              onchange={(selectedDateRange) => {
                                if (selectedDateRange[0]) {
                                  setDateError("");
                                  const selectedLocation =
                                    locationDetail.filter((e) => {
                                      const optionValue =
                                        getLocationOptionValue(e);
                                      return (
                                        optionValue?.toLowerCase() ===
                                        pickupLocation?.toLowerCase()
                                      );
                                    })[0];

                                  if (
                                    selectedLocation &&
                                    selectedLocation.onlyDropOffOutOfHours &&
                                    Array.isArray(selectedLocation.closing)
                                  ) {
                                    const startDay = getDay(
                                      selectedDateRange[0]
                                    );

                                    // Check if the selected date falls in any closed period
                                    const isDateClosed =
                                      selectedLocation.closing.some((entry) => {
                                        if (
                                          entry.dayOfTheWeek % 7 ===
                                          startDay
                                        ) {
                                          if (
                                            entry.validFrom &&
                                            entry.validTo
                                          ) {
                                            const validFrom = new Date(
                                              entry.validFrom
                                            );
                                            const validTo = new Date(
                                              entry.validTo
                                            );
                                            const selectedDate =
                                              selectedDateRange[0];

                                            return (
                                              selectedDate >= validFrom &&
                                              selectedDate <= validTo
                                            );
                                          }
                                          return true;
                                        }
                                        return false;
                                      });

                                    if (isDateClosed) {
                                      setDateError(
                                        "The date is not available for booking."
                                      );
                                      setStartDate(null);
                                      return;
                                    }
                                  }
                                  if (selectedLocation) {
                                    const opening =
                                      selectedLocation.openings.filter(
                                        (e) =>
                                          getDay(selectedDateRange[0]) ===
                                          e.dayOfTheWeek % 7
                                      )[0];

                                    const startTime = opening
                                      ? opening.LocationStartTime ||
                                        opening.startTime
                                      : "";

                                    setStartDate(
                                      setTime(startTime, selectedDateRange[0])
                                    );
                                  }
                                } else {
                                  setStartDate(null);
                                }
                                if (selectedDateRange[1]) {
                                  const selectedLocation =
                                    locationDetail.filter((e) => {
                                      const optionValue =
                                        getLocationOptionValue(e);
                                      return (
                                        optionValue?.toLowerCase() ===
                                        reservationDestination.toLowerCase()
                                      );
                                    })[0];

                                  if (selectedLocation) {
                                    const opening =
                                      selectedLocation.openings.filter(
                                        (e) =>
                                          getDay(selectedDateRange[1]) ===
                                          e.dayOfTheWeek % 7
                                      )[0];

                                    const endTime = opening
                                      ? opening.LocationStartTime ||
                                        opening.startTime
                                      : "";

                                    setEndDate(
                                      setTime(endTime, selectedDateRange[1])
                                    );
                                  }
                                } else {
                                  setEndDate(null);
                                }
                              }}
                            />
                            <AppInputSwitcher
                              required={true}
                              disabled={!reservationStartDate}
                              className="w-full lg:!w-[100%] min-w-[5rem] sm:w-[100%] md:w-full"
                              label={pickUpTime}
                              key={"pickUpTime"}
                              enableTimesInRange={getEnabledTimeSlots(
                                locationDetail,
                                pickupLocation,
                                reservationStartDate
                              )}
                              value={reservationStartDate}
                              options={[]}
                              type="time"
                              variant="slot"
                              onchange={(e) => {
                                setStartDate(e);
                              }}
                            />
                            <AppInputSwitcher
                              enableTimesInRange={getEnabledDropoffTimeSlots(
                                locationDetail,
                                reservationDestination,
                                reservationEndDate
                              )}
                              disabled={!reservationEndDate}
                              required={true}
                              className="w-full lg:!w-[100%]  min-w-[5rem] sm:w-[100%] md:w-full"
                              label={dropOffTime}
                              key={"dropOffTime"}
                              options={[]}
                              value={reservationEndDate}
                              type="time"
                              variant="slot"
                              onchange={(e) => {
                                setEndDate(e);
                              }}
                            />
                          </>
                        )}
                        {/* Macro group selection moved to icon tabs above card */}

                        {disableCoupon === false &&
                          (mountedRef.current &&
                          AppSettings?.showReservationGroupSelection &&
                          reservationSourceEnabled === true &&
                          reservationSourceForUser?.length > 0 ? (
                            <div className="flex flex-col gap-1 w-full lg:w-auto lg:min-w-[10rem]">
                              <AppInputSwitcher
                                required={true}
                                className="w-full"
                                enablePerDayReservationSource={
                                  AppSettings?.enablePerDayReservationSource
                                }
                                label=""
                                key={"reservationPlatform"}
                                options={reservationSourceForUser?.map(
                                  (e) =>
                                    e["Ragione Sociale textRS"] ||
                                    e["description"] ||
                                    e["Ragione Sociale"]
                                )}
                                type="select"
                                variant="with-search"
                                onchange={(e) => {
                                  setReservationPlatform(e);
                                }}
                                value={reservationPlatform}
                              />
                            </div>
                          ) : (
                            AppSettings?.showReservationGroupSelection &&
                            !isScriptChannelResSource && (
                              <div className="flex flex-col gap-1 w-full lg:w-auto lg:min-w-[10rem]">
                                <AppInputSwitcher
                                  required={true}
                                  enablePerDayReservationSource={
                                    AppSettings?.enablePerDayReservationSource
                                  }
                                  className="w-full"
                                  label=""
                                  key={"reservationPlatform"}
                                  options={reservationSourceOptions?.map(
                                    (e) =>
                                      e["Ragione Sociale textRS"] ||
                                      e["Ragione Sociale"]
                                  )}
                                  value={reservationPlatform}
                                  type="select"
                                  variant="with-search"
                                  onchange={(e) => {
                                    setReservationPlatform(e);
                                  }}
                                />
                              </div>
                            )
                          ))}

                        {AppSettings?.showWebCodeInput && (
                          <div className="flex flex-col gap-1 w-full lg:w-auto lg:min-w-[10rem] lg:max-w-[16rem]">
                            <AppInputSwitcher
                              required={false}
                              autoComplete="off"
                              className="w-full"
                              label=""
                              InputLabelProps={{ shrink: true }}
                              key={"Voucher Code"}
                              placeholder={translate("Discount Code")}
                              options={[]}
                              value={agreementCoupon}
                              type="text"
                              variant="slot"
                              onchange={(e: string) => {
                                setagreementCoupon(e);
                              }}
                              disabledCoupon={disableCoupon}
                            />
                          </div>
                        )}
                        {/* Modal for invalid discount code */}
                        {showModal && validateAgreementCoupon && (
                          <div
                            className="modal-overlay"
                            style={{
                              position: "fixed",
                              top: "0",
                              left: "0",
                              right: "0",
                              bottom: "0",
                              backgroundColor: "rgba(0, 0, 0, 0.5)",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              zIndex: "1001",
                            }}
                          >
                            <div
                              className="modal-content"
                              style={{
                                background: "white",
                                width: "300px",
                                padding: "15px",
                                borderRadius: "8px",
                                textAlign: "center",
                                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                                zIndex: "1000",
                              }}
                            >
                              <p>{modalMessage}</p>
                              <button
                                onClick={() => setShowModal(false)}
                                className="close-button"
                                style={{
                                  backgroundColor:
                                    dynamicTheme?.palette["primary"]?.main,
                                  color: "white",
                                  border: "none",
                                  padding: "10px 20px",
                                  borderRadius: "4px",
                                  cursor: "pointer",
                                  marginTop: "10px",
                                }}
                              >
                                {translate("Close")}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      <>
                        {!AppSettings?.showSeparatePickDropDate &&
                          dateError && (
                            <div className="text-red-500 text-sm mt-1">
                              {dateError}
                            </div>
                          )}
                      </>

                      <div className="flex flex-row gap-4 w-full flex-wrap items-center">
                        {AppSettings?.locationReturnEnable &&
                          !homeReturnSelected && (
                            <div className="min-w-fit max-w-fit">
                              <div className="mt-4">
                                <AppStandardMultiSelectCheckBox
                                  value={[
                                    isDroppingAtSameLocation
                                      ? translate("drop at same location")
                                      : "",
                                  ]}
                                  onChange={handleDropAtSameLocationChange}
                                  options={[translate("drop at same location")]}
                                  color={
                                    AppSettings?.secondaryAsBackgroundForButtons
                                      ? "secondaryColor"
                                      : "primary"
                                  }
                                />
                              </div>
                            </div>
                          )}

                        <div>
                          <AppRadioButton
                            required={true}
                            value={isAgeLimitSelected}
                            color={
                              AppSettings?.secondaryAsBackgroundForButtons
                                ? "secondaryColor"
                                : "primary"
                            }
                            onChange={(e) => {
                              setIsAgeLimitSelected(e);
                            }}
                            options={Object.entries(ageCriteriaData)
                              .map(([key, value]) => {
                                switch (key.toUpperCase()) {
                                  case "YOUNGDRIVER":
                                    return {
                                      label: t(value),
                                      value: "youngDriver",
                                    };
                                  case "YOUNGDRIVER2":
                                    return {
                                      label: t(value),
                                      value: "youngDriver2",
                                    };
                                  case "NOAGEDRIVER":
                                    return {
                                      label: t(value),
                                      value: "noAgeFeeDriver",
                                    };
                                  case "SENIORDRIVER":
                                    return {
                                      label: t(value),
                                      value: "seniorDriver",
                                    };
                                  default:
                                    return {
                                      label: "",
                                      value: "",
                                    };
                                }
                              })
                              .filter((option) => option !== null)}
                          />
                        </div>
                        <AppButton
                          type={"submit"}
                          className={`h-12 md:ml-auto ${
                            smallScreen || istabletScreen ? "w-full" : ""
                          }`}
                          color={"primary"}
                          style={{
                            borderRadius: "12px",
                          }}
                        >
                          <p className={`w-full text-center`}>
                            {translate("Show car")}
                          </p>
                        </AppButton>
                        {AppSettings?.showTextBeginningOfBookingForm && (
                          <div className="w-full">
                            <p
                              className="self-start"
                              style={{
                                color:
                                  dynamicTheme?.palette["secondaryColor"]?.main,
                              }}
                            >
                              {showTextBeginningOfBookingFormMsg}
                            </p>
                          </div>
                        )}
                      </div>
                    </AppCard>
                  </div>
                  {showMapPanel && (
                    <div
                      className="w-full md:w-1/2 md:min-w-0 md:self-stretch flex flex-col border-t border-gray-200 md:border-t-0"
                      style={{
                        overflow: "hidden",
                        minHeight: isDroppingAtSameLocation ? 420 : 560,
                      }}
                    >
                      {/* Pickup map */}
                      <div
                        style={{
                          flex: isDroppingAtSameLocation
                            ? "1 1 100%"
                            : "1 1 50%",
                          minHeight: isDroppingAtSameLocation ? 420 : 280,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <div
                          style={{
                            padding: "6px 12px",
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#555",
                            background: "#f8f8f8",
                            borderBottom: "1px solid #e0e0e0",
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            flexShrink: 0,
                          }}
                        >
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              background:
                                dynamicTheme?.palette?.["primary"]?.main ??
                                "#E24B4A",
                              display: "inline-block",
                              flexShrink: 0,
                            }}
                          />
                          {translate("Pickup Location")}
                          {isDroppingAtSameLocation && (
                            <span> &amp; {translate("Drop-off Location")}</span>
                          )}
                        </div>
                        <div
                          style={{
                            flex: 1,
                            minHeight: isDroppingAtSameLocation ? 420 : 280,
                          }}
                        >
                          <BranchMapPanel
                            mapState={mapPanelState}
                            allLocations={locationDetail}
                            rankedBranches={branchRankedLocations}
                            addressCoords={branchAddressCoords}
                            selectedBranch={selectedBranchForMap}
                            primaryColor={
                              dynamicTheme?.palette?.["primary"]?.main ??
                              "#E24B4A"
                            }
                            minHeight={isDroppingAtSameLocation ? 420 : 280}
                          />
                        </div>
                      </div>
                      {/* Dropoff map — visible only when separate dropoff location */}
                      {!isDroppingAtSameLocation && (
                        <div
                          style={{
                            flex: "1 1 50%",
                            minHeight: 280,
                            borderTop: "2px solid #e0e0e0",
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <div
                            style={{
                              padding: "6px 12px",
                              fontSize: 12,
                              fontWeight: 600,
                              color: "#555",
                              background: "#f8f8f8",
                              borderBottom: "1px solid #e0e0e0",
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              flexShrink: 0,
                            }}
                          >
                            <span
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                background:
                                  dynamicTheme?.palette?.["secondaryColor"]
                                    ?.main ?? "#1860ab",
                                display: "inline-block",
                                flexShrink: 0,
                              }}
                            />
                            {translate("Drop-off Location")}
                          </div>
                          <div style={{ flex: 1, minHeight: 280 }}>
                            <BranchMapPanel
                              mapState={effectiveDropoffMapState}
                              allLocations={
                                locationDropOffDetail.length
                                  ? locationDropOffDetail
                                  : locationDetail
                              }
                              rankedBranches={dropoffRankedLocations}
                              addressCoords={dropoffAddressCoords}
                              selectedBranch={effectiveDropoffSelectedBranch}
                              primaryColor={
                                dynamicTheme?.palette?.["secondaryColor"]
                                  ?.main ?? "#1860ab"
                              }
                              minHeight={280}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                {AppSettings?.pageTitle && (
                  <>
                    <span className="text-white font-bold text-xl">
                      {t("Book Online")}
                    </span>
                    <span className="text-white font-bold text-xl">-</span>
                    <span className="text-white font-bold text-xl">
                      {pageTitleLabel}
                    </span>
                    <br />
                    <br />
                  </>
                )}
                <div
                  className={
                    showMapPanel
                      ? "flex flex-col md:flex-row w-full md:w-[72vw] md:max-w-[1120px] md:items-stretch"
                      : "w-full"
                  }
                >
                  <div
                    className={
                      showMapPanel
                        ? "w-full md:w-1/2 min-w-0 flex flex-col"
                        : "w-full"
                    }
                  >
                    <AppCard className="flex flex-col bg-white w-full box-border justify-center items-center p-4 gap-4 py-8">
                      <>
                        {deliveryOptionsWidget}
                        {showMapPanel && (
                          <BranchAddressSearch
                            locations={locationDetail}
                            initialAddress={branchSearchAddress}
                            primaryColor={
                              dynamicTheme?.palette?.["primary"]?.main ??
                              undefined
                            }
                            secondaryColor={
                              dynamicTheme?.palette?.["secondaryColor"]?.main ??
                              undefined
                            }
                            onAddressSelected={handleBranchAddressSelected}
                            onAddressCleared={handleBranchAddressCleared}
                          />
                        )}
                        <div
                          className={
                            showMapPanel
                              ? "flex flex-col gap-2 w-full"
                              : "flex xl:flex-row gap-2 flex-col w-full"
                          }
                        >
                          {isAffiliateEnable && !scriptAffiliate && (
                            <AppInputSwitcher
                              required={true}
                              className={
                                "md:grow flex md:flex-row gap-2 flex-col min-w-[0rem] md:min-w-[19rem]"
                              }
                              value={selectedAffiliateName}
                              label={translate(
                                "Choose the partner to rent with"
                              )}
                              key={"affiliate"}
                              options={affiliateOptions}
                              type="select"
                              variant="with-search"
                              disabled={true}
                              onchange={(selectedName) => {
                                const selectedAffiliate = affiliateDetail.find(
                                  (e) => e[1] === selectedName
                                );
                                if (selectedAffiliate) {
                                  const id = selectedAffiliate[0];
                                  const name = selectedAffiliate[1];
                                  setSelectedAffiliateName(name);
                                  setSelectedAffiliate(id);
                                  setPickupLocation(null);
                                  setStartDate(null);
                                  setEndDate(null);
                                }
                              }}
                            />
                          )}

                          {showMapPanel &&
                            !isDroppingAtSameLocation &&
                            !isHomeReturnWithAddressActive && (
                              <BranchAddressSearch
                                label={translate(
                                  "Search nearby drop-off branches"
                                )}
                                locations={
                                  locationDropOffDetail.length
                                    ? locationDropOffDetail
                                    : locationDetail
                                }
                                initialAddress={dropoffSearchAddress}
                                primaryColor={
                                  dynamicTheme?.palette?.["secondaryColor"]
                                    ?.main ?? undefined
                                }
                                secondaryColor={
                                  dynamicTheme?.palette?.["secondaryColor"]
                                    ?.main ?? undefined
                                }
                                onAddressSelected={handleDropoffAddressSelected}
                                onAddressCleared={handleDropoffAddressCleared}
                              />
                            )}

                          <div
                            className={
                              showMapPanel
                                ? "grid grid-cols-2 gap-4 w-full"
                                : `flex md:flex-row gap-2 flex-col min-w-[0rem] ${
                                    AppSettings?.showSeparatePickDropDate
                                      ? "md:flex-none md:w-[16rem] xl:w-[13rem] 2xl:w-[16rem]"
                                      : `md:grow ${
                                          isAffiliateEnable
                                            ? "md:min-w-[25rem]"
                                            : "md:min-w-[25rem]"
                                        }`
                                  }`
                            }
                          >
                            {showLocationPanel ? (
                              <LocationSelectWithPanel
                                key={"pickUpLocation"}
                                required={true}
                                className={
                                  showMapPanel &&
                                  isDroppingAtSameLocation &&
                                  !isHomeReturnWithAddressActive
                                    ? "w-full col-span-2"
                                    : "w-full"
                                }
                                value={
                                  homeDeliverySelected &&
                                  !isPickupManuallySelected
                                    ? ""
                                    : pickupLocation
                                }
                                label={translate("pick up location")}
                                options={effectivePickupLocations || []}
                                disableSoftKeyboard={
                                  !!(
                                    AppSettings?.notOpenKeyboardForLocation &&
                                    smallScreen
                                  )
                                }
                                // Preserves the exact option-value string
                                // shape used throughout BookingForm today —
                                // do NOT change without also touching
                                // getLocationByPickup/Dropoff (CLAUDE.md §14).
                                // Reuses BookingForm.getLocationOptionValue —
                                // same string shape getLocationByPickup/Dropoff +
                                // calendar matchers expect (CLAUDE.md §14).
                                getOptionValue={getLocationOptionValue}
                                getOptionLabel={getPickupLocationLabel}
                                getSortValue={getPickupDistanceSortValue}
                                anchorDate={reservationStartDate}
                                recentKey={"pickup"}
                                onChange={handlePickupChange}
                              />
                            ) : (
                              /* BOOKING-984 rollback — pre-BOOKING-984 UI restored verbatim. */
                              <AppInputSwitcher
                                required={true}
                                className={
                                  showMapPanel &&
                                  isDroppingAtSameLocation &&
                                  !isHomeReturnWithAddressActive
                                    ? "w-full col-span-2"
                                    : "w-full"
                                }
                                value={
                                  homeDeliverySelected &&
                                  !isPickupManuallySelected
                                    ? ""
                                    : pickupDisplayValue
                                }
                                label={translate("pick up location")}
                                key={"pickUpLocation"}
                                options={effectivePickupLocations.map(
                                  (e: Location) => {
                                    let icon = (
                                      <LocationCityIcon color="primary" />
                                    );
                                    if (e.isAirport) {
                                      icon = (
                                        <FlightTakeoffIcon htmlColor="blue" />
                                      );
                                    } else if (e.isRailway) {
                                      icon = <TrainIcon htmlColor="green" />;
                                    }
                                    return {
                                      // label carries the distance badge; value stays clean
                                      label: getPickupLocationLabel(e),
                                      value: getLocationOptionValue(e),
                                      icon,
                                    };
                                  }
                                )}
                                type="select"
                                variant={
                                  AppSettings?.notOpenKeyboardForLocation &&
                                  smallScreen
                                    ? "with-search-label"
                                    : "with-search"
                                }
                                onchange={handlePickupChange}
                              />
                            )}
                            {isHomeReturnWithAddressActive ? (
                              showLocationPanel ? (
                                <LocationSelectWithPanel
                                  key={"dropoff_return_reactive"}
                                  required={true}
                                  className="w-full"
                                  value={
                                    !isDropoffManuallySelected
                                      ? ""
                                      : reservationDestination
                                  }
                                  label={translate("Dropoff Location")}
                                  options={effectiveDropoffLocations || []}
                                  getOptionValue={getLocationOptionValue}
                                  getOptionLabel={getReturnOptionLabel}
                                  getSortValue={getReturnDistanceSortValue}
                                  anchorDate={reservationStartDate}
                                  recentKey={"dropoff"}
                                  onChange={handleReactiveDropoffChange}
                                />
                              ) : (
                                <AppInputSwitcher
                                  required={true}
                                  className="w-full"
                                  value={
                                    !isDropoffManuallySelected
                                      ? ""
                                      : reservationDestination
                                  }
                                  label={translate("Dropoff Location")}
                                  key={"dropoff_return_reactive"}
                                  options={effectiveDropoffLocations.map(
                                    (e) => {
                                      let icon = (
                                        <LocationCityIcon color="primary" />
                                      );
                                      if (e.isAirport) {
                                        icon = (
                                          <FlightTakeoffIcon htmlColor="blue" />
                                        );
                                      } else if (e.isRailway) {
                                        icon = <TrainIcon htmlColor="green" />;
                                      }
                                      return {
                                        label: getReturnOptionLabel(e),
                                        value: getLocationOptionValue(e),
                                        icon,
                                      };
                                    }
                                  )}
                                  type="select"
                                  variant="with-search"
                                  onchange={handleReactiveDropoffChange}
                                />
                              )
                            ) : !isDroppingAtSameLocation ? (
                              showLocationPanel ? (
                                <LocationSelectWithPanel
                                  key={"dropOffLocation"}
                                  required={true}
                                  className="w-full"
                                  value={reservationDestination}
                                  label={translate("drop off location")}
                                  options={effectiveDropoffLocations || []}
                                  // Reuses BookingForm.getLocationOptionValue —
                                  // same string shape getLocationByPickup/Dropoff +
                                  // calendar matchers expect (CLAUDE.md §14).
                                  getOptionValue={getLocationOptionValue}
                                  getOptionLabel={getDropoffLocationLabel}
                                  getSortValue={getDropoffDistanceSortValue}
                                  anchorDate={reservationStartDate}
                                  recentKey={"dropoff"}
                                  onChange={handleDropoffChange}
                                />
                              ) : (
                                /* BOOKING-984 rollback — pre-BOOKING-984 UI restored verbatim. */
                                <AppInputSwitcher
                                  required={true}
                                  className="w-full"
                                  value={dropoffDisplayValue}
                                  label={translate("drop off location")}
                                  key={"dropOffLocation"}
                                  options={effectiveDropoffLocations.map(
                                    (e) => {
                                      let icon = (
                                        <LocationCityIcon color="primary" />
                                      );
                                      if (e.isAirport) {
                                        icon = (
                                          <FlightTakeoffIcon htmlColor="blue" />
                                        );
                                      } else if (e.isRailway) {
                                        icon = <TrainIcon htmlColor="green" />;
                                      }
                                      return {
                                        label: getDropoffLocationLabel(e),
                                        value: getLocationOptionValue(e),
                                        icon,
                                      };
                                    }
                                  )}
                                  type="select"
                                  variant="with-search"
                                  onchange={handleDropoffChange}
                                />
                              )
                            ) : null}
                          </div>

                          <div
                            className={
                              showMapPanel
                                ? "grid grid-cols-2 gap-4 w-full"
                                : "contents"
                            }
                          >
                            {AppSettings?.showSeparatePickDropDate ? (
                              <>
                                {/* Start Date Picker */}
                                <div
                                  ref={pickupDateGroupRef}
                                  className={
                                    showMapPanel
                                      ? "order-1 w-full flex flex-col relative"
                                      : "flex flex-col relative grow md:min-w-36 xl:min-w-[7rem] 2xl:min-w-36"
                                  }
                                >
                                  <DatePickerComponent
                                    sx={{
                                      svg: { color: "primary" },
                                      input: { color: "primary" },
                                      label: { color: "primary" },
                                      backgroundColor: "primary",
                                    }}
                                    required={true}
                                    disableOnDay={disableDate}
                                    shouldDisableDateFn={
                                      pickupShouldDisableDate
                                    }
                                    className="w-full"
                                    label={pickUpDate}
                                    value={reservationStartDate}
                                    minDate={dayjs().add(
                                      minRentalAllowedDay,
                                      "day"
                                    )}
                                    maxDate={dayjs().add(365, "day")}
                                    onChange={(selectedStartDate) => {
                                      if (selectedStartDate) {
                                        setStartDateError("");
                                        const selectedLocation =
                                          locationDetail.find((e) => {
                                            const optionValue =
                                              getLocationOptionValue(e);
                                            return (
                                              optionValue?.toLowerCase() ===
                                              pickupLocation?.toLowerCase()
                                            );
                                          });

                                        if (
                                          selectedLocation &&
                                          selectedLocation.onlyDropOffOutOfHours &&
                                          Array.isArray(
                                            selectedLocation.closing
                                          )
                                        ) {
                                          const startDay = getDay(
                                            selectedStartDate.toDate()
                                          );

                                          const isDateClosed =
                                            selectedLocation.closing.some(
                                              (entry) => {
                                                if (
                                                  entry.dayOfTheWeek % 7 ===
                                                  startDay
                                                ) {
                                                  if (
                                                    entry.validFrom &&
                                                    entry.validTo
                                                  ) {
                                                    const validFrom = new Date(
                                                      entry.validFrom
                                                    );
                                                    const validTo = new Date(
                                                      entry.validTo
                                                    );
                                                    const selectedDate =
                                                      selectedStartDate.toDate();

                                                    return (
                                                      selectedDate >=
                                                        validFrom &&
                                                      selectedDate <= validTo
                                                    );
                                                  }
                                                  return true;
                                                }
                                                return false;
                                              }
                                            );

                                          if (isDateClosed) {
                                            setStartDateError(
                                              translate(
                                                "The date is not available for booking."
                                              )
                                            );
                                            setStartDate(null);
                                            return;
                                          }
                                        }

                                        if (selectedLocation) {
                                          const openingObj =
                                            selectedLocation.openings?.find(
                                              (e) =>
                                                getDay(
                                                  selectedStartDate.toDate()
                                                ) ===
                                                e.dayOfTheWeek % 7
                                            );

                                          const opening =
                                            selectedLocation.isOutOfHours ||
                                            selectedLocation.pickUpOutOfHours
                                              ? openingObj?.LocationStartTime
                                              : openingObj?.startTime;

                                          setStartDate(
                                            setTime(
                                              opening || "",
                                              selectedStartDate.toDate()
                                            )
                                          );
                                        }
                                      } else {
                                        setStartDate(null);
                                      }
                                    }}
                                  />
                                  {startDateError && (
                                    <p className="text-red-500 text-xs mt-1 whitespace-nowrap">
                                      {startDateError}
                                    </p>
                                  )}
                                </div>
                                {/* End Date Picker */}
                                <div
                                  ref={dropoffDateGroupRef}
                                  className={
                                    showMapPanel
                                      ? "order-3 w-full flex flex-col relative"
                                      : "flex flex-col relative grow md:min-w-36 xl:min-w-[7rem] 2xl:min-w-36"
                                  }
                                >
                                  <DatePickerComponent
                                    sx={{
                                      svg: { color: "primary" },
                                      input: { color: "primary" },
                                      label: { color: "primary" },
                                      backgroundColor: "primary",
                                    }}
                                    className="w-full"
                                    required={true}
                                    disableOnDay={disableDropoffDate}
                                    shouldDisableDateFn={
                                      dropoffShouldDisableDate
                                    }
                                    label={dropOffDate}
                                    value={reservationEndDate}
                                    minDate={
                                      reservationStartDate
                                        ? dayjs(reservationStartDate)
                                        : dayjs().add(
                                            minRentalAllowedDay,
                                            "day"
                                          )
                                    }
                                    maxDate={
                                      reservationStartDate
                                        ? dayjs(reservationStartDate).add(
                                            maxRentalAllowedDay,
                                            "day"
                                          )
                                        : dayjs().add(365, "day")
                                    }
                                    onChange={(selectedEndDate) => {
                                      if (selectedEndDate) {
                                        setEndDateError("");
                                        const selectedLocation =
                                          locationDetail.find((e) => {
                                            const optionValue =
                                              getLocationOptionValue(e);
                                            return (
                                              optionValue?.toLowerCase() ===
                                              reservationDestination?.toLowerCase()
                                            );
                                          });

                                        const opening =
                                          selectedLocation?.openings?.find(
                                            (e) =>
                                              getDay(
                                                selectedEndDate.toDate()
                                              ) ===
                                              e.dayOfTheWeek % 7
                                          )?.startTime;
                                        setEndDate(
                                          setTime(
                                            opening || "",
                                            selectedEndDate.toDate()
                                          )
                                        );
                                      } else {
                                        setEndDate(null);
                                      }
                                    }}
                                  />
                                  {endDateError && (
                                    <p className="text-red-500 text-xs mt-1 whitespace-nowrap">
                                      {endDateError}
                                    </p>
                                  )}
                                </div>
                              </>
                            ) : (
                              <div
                                className={
                                  showMapPanel
                                    ? "col-span-2 relative reservation-date-field"
                                    : "relative reservation-date-field grow md:min-w-72"
                                }
                              >
                                <AppInputSwitcher
                                  disableOnDay={disableDate}
                                  disableOnDayEnd={disableDropoffDate}
                                  shouldDisableStartDateFn={
                                    pickupShouldDisableDate
                                  }
                                  shouldDisableEndDateFn={
                                    dropoffShouldDisableDate
                                  }
                                  required={true}
                                  className="w-full"
                                  label={translate("reservation dates")}
                                  key={"reservationDates"}
                                  options={[]}
                                  startDate={reservationStartDate}
                                  endDate={reservationEndDate}
                                  minDate={addDays(
                                    new Date(),
                                    minRentalAllowedDay
                                  )}
                                  maxDate={
                                    reservationStartDate
                                      ? addDays(
                                          new Date(
                                            reservationStartDate?.toString()
                                          ),
                                          maxRentalAllowedDay
                                        )
                                      : addDays(new Date(), 365)
                                  }
                                  type="date"
                                  variant="range"
                                  onchange={(selectedDateRange) => {
                                    if (selectedDateRange[0]) {
                                      setDateError("");
                                      const selectedLocation =
                                        locationDetail.filter((e) => {
                                          const optionValue =
                                            getLocationOptionValue(e);
                                          return (
                                            optionValue?.toLowerCase() ===
                                            pickupLocation?.toLowerCase()
                                          );
                                        })[0];

                                      if (
                                        selectedLocation &&
                                        selectedLocation.onlyDropOffOutOfHours &&
                                        Array.isArray(selectedLocation.closing)
                                      ) {
                                        const startDay = getDay(
                                          selectedDateRange[0]
                                        );

                                        const isDateClosed =
                                          selectedLocation.closing.some(
                                            (entry) => {
                                              if (
                                                entry.dayOfTheWeek % 7 ===
                                                startDay
                                              ) {
                                                if (
                                                  entry.validFrom &&
                                                  entry.validTo
                                                ) {
                                                  const validFrom = new Date(
                                                    entry.validFrom
                                                  );
                                                  const validTo = new Date(
                                                    entry.validTo
                                                  );
                                                  const selectedDate =
                                                    selectedDateRange[0];

                                                  return (
                                                    selectedDate >= validFrom &&
                                                    selectedDate <= validTo
                                                  );
                                                }
                                                return true;
                                              }
                                              return false;
                                            }
                                          );

                                        if (isDateClosed) {
                                          setDateError(
                                            "The date is not available for booking."
                                          );
                                          setStartDate(null);
                                          return;
                                        }
                                      }

                                      if (selectedLocation) {
                                        const opening =
                                          selectedLocation.openings.filter(
                                            (e) =>
                                              getDay(selectedDateRange[0]) ===
                                              e.dayOfTheWeek % 7
                                          )[0];

                                        const startTime = opening
                                          ? opening.LocationStartTime ||
                                            opening.startTime
                                          : "";

                                        setStartDate(
                                          setTime(
                                            startTime,
                                            selectedDateRange[0]
                                          )
                                        );
                                      }
                                    } else {
                                      setStartDate(null);
                                    }
                                    if (selectedDateRange[1]) {
                                      const selectedLocation =
                                        locationDetail.filter((e) => {
                                          const optionValue =
                                            getLocationOptionValue(e);
                                          return (
                                            optionValue?.toLowerCase() ===
                                            reservationDestination.toLowerCase()
                                          );
                                        })[0];

                                      if (selectedLocation) {
                                        const opening =
                                          selectedLocation.openings.filter(
                                            (e) =>
                                              getDay(selectedDateRange[1]) ===
                                              e.dayOfTheWeek % 7
                                          )[0];

                                        const endTime = opening
                                          ? opening.LocationStartTime ||
                                            opening.startTime
                                          : "";

                                        setEndDate(
                                          setTime(endTime, selectedDateRange[1])
                                        );
                                      }
                                    } else {
                                      setEndDate(null);
                                    }
                                  }}
                                />
                                {dateError && (
                                  <p className="text-red-500 text-xs absolute left-0 top-full mt-1 whitespace-nowrap">
                                    {dateError}
                                  </p>
                                )}
                              </div>
                            )}
                            <div
                              className={
                                showMapPanel ? "order-2 w-full" : "contents"
                              }
                            >
                              <AppInputSwitcher
                                required={true}
                                disabled={!reservationStartDate}
                                className={
                                  showMapPanel
                                    ? "w-full"
                                    : `w-full lg:!w-[100%] min-w-[5rem] sm:w-[100%] md:w-full${
                                        AppSettings?.showSeparatePickDropDate
                                          ? " xl:!w-[7rem] 2xl:!w-[10rem]"
                                          : ""
                                      }`
                                }
                                label={pickUpTime}
                                key={"pickUpTime"}
                                enableTimesInRange={getEnabledTimeSlots(
                                  locationDetail,
                                  pickupLocation,
                                  reservationStartDate
                                )}
                                value={reservationStartDate}
                                options={[]}
                                type="time"
                                variant="slot"
                                onchange={(e) => {
                                  setStartDate(e);
                                }}
                              />
                            </div>
                            <div
                              className={
                                showMapPanel ? "order-4 w-full" : "contents"
                              }
                            >
                              <AppInputSwitcher
                                enableTimesInRange={getEnabledDropoffTimeSlots(
                                  locationDropOffDetail || locationDetail,
                                  reservationDestination,
                                  reservationEndDate
                                )}
                                disabled={!reservationEndDate}
                                required={true}
                                className={
                                  showMapPanel
                                    ? "w-full"
                                    : `w-full lg:!w-[100%]  min-w-[5rem] sm:w-[100%] md:w-full${
                                        AppSettings?.showSeparatePickDropDate
                                          ? " xl:!w-[7rem] 2xl:!w-[10rem]"
                                          : ""
                                      }`
                                }
                                label={dropOffTime}
                                key={"dropOffTime"}
                                options={[]}
                                value={reservationEndDate}
                                type="time"
                                variant="slot"
                                onchange={(e) => {
                                  setEndDate(e);
                                }}
                              />
                            </div>
                          </div>
                          {AppSettings?.showMacroGroupSelection && (
                            <AppInputSwitcher
                              required={false}
                              className={"w-full"}
                              type="select"
                              variant="with-search"
                              label={macroGroupLabel}
                              options={(Array.isArray(macroGroupOptions)
                                ? macroGroupOptions
                                : []
                              ).map((e) => e.label)}
                              value={selectedMacroGroup}
                              onchange={(e) => {
                                const selectedOption = Array.isArray(
                                  macroGroupOptions
                                )
                                  ? macroGroupOptions.find(
                                      (option) => option.label === e
                                    )
                                  : null;
                                if (selectedOption) {
                                  setSelectedMacroGroup(selectedOption.label);
                                  setSelectedMacroGroupWebCode(
                                    selectedOption.WebCode
                                  );
                                }
                              }}
                            />
                          )}

                          {disableCoupon === false &&
                            (mountedRef.current &&
                            AppSettings?.showReservationGroupSelection &&
                            reservationSourceEnabled === true &&
                            reservationSourceForUser?.length > 0 ? (
                              <div className="flex">
                                <AppInputSwitcher
                                  required={true}
                                  className="w-full min-w-[12rem] xl:min-w-[9rem] 2xl:min-w-[12rem]"
                                  enablePerDayReservationSource={
                                    AppSettings?.enablePerDayReservationSource
                                  }
                                  label={
                                    userId
                                      ? bookingResSourceForReservedArea
                                      : bookingReservationSource
                                  }
                                  key={"reservationPlatform"}
                                  options={reservationSourceForUser?.map(
                                    (e) =>
                                      e["Ragione Sociale textRS"] ||
                                      e["description"] ||
                                      e["Ragione Sociale"]
                                  )}
                                  type="select"
                                  variant="with-search"
                                  onchange={(e) => {
                                    setReservationPlatform(e);
                                  }}
                                  value={reservationPlatform}
                                />
                              </div>
                            ) : (
                              AppSettings?.showReservationGroupSelection &&
                              !isScriptChannelResSource && (
                                <div className="flex">
                                  <AppInputSwitcher
                                    required={true}
                                    enablePerDayReservationSource={
                                      AppSettings?.enablePerDayReservationSource
                                    }
                                    className="w-full min-w-[12rem] xl:min-w-[9rem] 2xl:min-w-[12rem]"
                                    label={bookingReservationSource}
                                    key={"reservationPlatform"}
                                    options={reservationSourceOptions?.map(
                                      (e) =>
                                        e["Ragione Sociale textRS"] ||
                                        e["Ragione Sociale"]
                                    )}
                                    value={reservationPlatform}
                                    type="select"
                                    variant="with-search"
                                    onchange={(e) => {
                                      setReservationPlatform(e);
                                    }}
                                  />
                                </div>
                              )
                            ))}

                          {AppSettings?.showWebCodeInput && (
                            <AppInputSwitcher
                              required={false}
                              autoComplete="off"
                              className={
                                AppSettings?.showSeparatePickDropDate
                                  ? "min-w-[10rem] xl:!w-[9rem] xl:!flex-none 2xl:!w-[12rem]"
                                  : "min-w-[10rem]"
                              }
                              label={codiceLabel}
                              key={"Voucher Code"}
                              placeholder={codiceLabel}
                              options={[]}
                              value={agreementCoupon}
                              type="text"
                              variant="slot"
                              onchange={(e) => {
                                setagreementCoupon(e);
                              }}
                              disabledCoupon={disableCoupon}
                            />
                          )}
                          {showModal && validateAgreementCoupon && (
                            <div
                              className="modal-overlay"
                              style={{
                                position: "fixed",
                                top: "0",
                                left: "0",
                                right: "0",
                                bottom: "0",
                                backgroundColor: "rgba(0, 0, 0, 0.5)",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                zIndex: "1001",
                              }}
                            >
                              <div
                                className="modal-content"
                                style={{
                                  background: "white",
                                  width: "300px",
                                  padding: "15px",
                                  borderRadius: "8px",
                                  textAlign: "center",
                                  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                                  zIndex: "1000",
                                }}
                              >
                                <p>{modalMessage}</p>
                                <button
                                  onClick={() => setShowModal(false)}
                                  className="close-button"
                                  style={{
                                    backgroundColor:
                                      dynamicTheme?.palette["primary"]?.main,
                                    color: "white",
                                    border: "none",
                                    padding: "10px 20px",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    marginTop: "10px",
                                  }}
                                >
                                  {translate("Close")}
                                  {/*Close*/}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                        <div
                          className={
                            showMapPanel
                              ? "gap-2 w-full"
                              : "flex flex-row gap-2 w-full flex-wrap"
                          }
                        >
                          {AppSettings?.locationReturnEnable &&
                            !homeReturnSelected && (
                              <div className="min-w-fit max-w-fit mt-2 self-start sm:self-auto">
                                <AppStandardMultiSelectCheckBox
                                  value={[
                                    isDroppingAtSameLocation
                                      ? translate("drop at same location")
                                      : "",
                                  ]}
                                  onChange={handleDropAtSameLocationChange}
                                  options={[translate("drop at same location")]}
                                  color={
                                    AppSettings?.secondaryAsBackgroundForButtons
                                      ? "secondaryColor"
                                      : "primary"
                                  }
                                />
                              </div>
                            )}

                          <div className={showMapPanel ? "w-4/5" : ""}>
                            <AppRadioButton
                              required={true}
                              value={isAgeLimitSelected}
                              color={
                                AppSettings?.secondaryAsBackgroundForButtons
                                  ? "secondaryColor"
                                  : "primary"
                              }
                              onChange={(e) => {
                                setIsAgeLimitSelected(e);
                              }}
                              options={Object.entries(ageCriteriaData)
                                .map(([key, value]) => {
                                  switch (key.toUpperCase()) {
                                    case "YOUNGDRIVER":
                                      return {
                                        label: t(value),
                                        value: "youngDriver",
                                      };
                                    case "YOUNGDRIVER2":
                                      return {
                                        label: t(value),
                                        value: "youngDriver2",
                                      };
                                    case "NOAGEDRIVER":
                                      return {
                                        label: t(value),
                                        value: "noAgeFeeDriver",
                                      };
                                    case "SENIORDRIVER":
                                      return {
                                        label: t(value),
                                        value: "seniorDriver",
                                      };
                                    default:
                                      return {
                                        label: "",
                                        value: "",
                                      };
                                  }
                                })
                                .filter((option) => option !== null)}
                            />
                          </div>
                          <AppButton
                            type={"submit"}
                            endIcon={<SearchIcon />}
                            className={`h-14 md:float-right md:ml-auto ${
                              showMapPanel
                                ? "w-full"
                                : smallScreen || istabletScreen
                                ? "w-full"
                                : "w-[10%]"
                            }`}
                            color={"primary"}
                            style={{
                              borderRadius: `${
                                AppSettings?.buttonBorderRadius || 0
                              }px`,
                            }}
                          >
                            <p className="w-full text-center">
                              {showMapPanel
                                ? translate("Proceed")
                                : translate("Search")}
                            </p>
                          </AppButton>
                          {AppSettings?.showTextBeginningOfBookingForm && (
                            <div className="w-full">
                              <p
                                className="self-start"
                                style={{
                                  color:
                                    dynamicTheme?.palette["secondaryColor"]
                                      ?.main,
                                }}
                              >
                                {showTextBeginningOfBookingFormMsg}
                              </p>
                            </div>
                          )}
                        </div>
                      </>
                    </AppCard>
                  </div>
                  {showMapPanel && (
                    <div
                      className="w-full md:w-1/2 md:min-w-0 md:self-stretch flex flex-col border-t border-gray-200 md:border-t-0"
                      style={{
                        overflow: "hidden",
                        minHeight: isDroppingAtSameLocation ? 420 : 560,
                      }}
                    >
                      {/* Pickup map */}
                      <div
                        style={{
                          flex: isDroppingAtSameLocation
                            ? "1 1 100%"
                            : "1 1 50%",
                          minHeight: isDroppingAtSameLocation ? 420 : 280,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <div
                          style={{
                            padding: "6px 12px",
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#555",
                            background: "#f8f8f8",
                            borderBottom: "1px solid #e0e0e0",
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            flexShrink: 0,
                          }}
                        >
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              background:
                                dynamicTheme?.palette?.["primary"]?.main ??
                                "#E24B4A",
                              display: "inline-block",
                              flexShrink: 0,
                            }}
                          />
                          {translate("Pickup Location")}
                          {isDroppingAtSameLocation && (
                            <span> &amp; {translate("Drop-off Location")}</span>
                          )}
                        </div>
                        <div
                          style={{
                            flex: 1,
                            minHeight: isDroppingAtSameLocation ? 420 : 280,
                          }}
                        >
                          <BranchMapPanel
                            mapState={mapPanelState}
                            allLocations={locationDetail}
                            rankedBranches={branchRankedLocations}
                            addressCoords={branchAddressCoords}
                            selectedBranch={selectedBranchForMap}
                            primaryColor={
                              dynamicTheme?.palette?.["primary"]?.main ??
                              "#E24B4A"
                            }
                            minHeight={isDroppingAtSameLocation ? 420 : 280}
                          />
                        </div>
                      </div>
                      {!isDroppingAtSameLocation && (
                        <div
                          style={{
                            flex: "1 1 50%",
                            minHeight: 280,
                            borderTop: "2px solid #e0e0e0",
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <div
                            style={{
                              padding: "6px 12px",
                              fontSize: 12,
                              fontWeight: 600,
                              color: "#555",
                              background: "#f8f8f8",
                              borderBottom: "1px solid #e0e0e0",
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              flexShrink: 0,
                            }}
                          >
                            <span
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                background:
                                  dynamicTheme?.palette?.["secondaryColor"]
                                    ?.main ?? "#1860ab",
                                display: "inline-block",
                                flexShrink: 0,
                              }}
                            />
                            {translate("Drop-off Location")}
                          </div>
                          <div style={{ flex: 1, minHeight: 280 }}>
                            <BranchMapPanel
                              mapState={effectiveDropoffMapState}
                              allLocations={
                                locationDropOffDetail.length
                                  ? locationDropOffDetail
                                  : locationDetail
                              }
                              rankedBranches={dropoffRankedLocations}
                              addressCoords={dropoffAddressCoords}
                              selectedBranch={effectiveDropoffSelectedBranch}
                              primaryColor={
                                dynamicTheme?.palette?.["secondaryColor"]
                                  ?.main ?? "#1860ab"
                              }
                              minHeight={280}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )
          ) : (
            <div
              className={
                showMapPanel
                  ? "flex flex-col md:flex-row w-full md:items-stretch"
                  : "w-full"
              }
            >
              <div
                className={
                  showMapPanel
                    ? "w-full md:flex-1 min-w-0 flex flex-col"
                    : "w-full"
                }
              >
                <AppCard className="flex flex-col bg-white w-full box-border justify-center items-center p-4 gap-4 py-8">
                  <>
                    {deliveryOptionsWidget}
                    {showMapPanel && (
                      <BranchAddressSearch
                        locations={locationDetail}
                        initialAddress={branchSearchAddress}
                        primaryColor={
                          dynamicTheme?.palette?.["primary"]?.main ?? undefined
                        }
                        secondaryColor={
                          dynamicTheme?.palette?.["secondaryColor"]?.main ??
                          undefined
                        }
                        onAddressSelected={handleBranchAddressSelected}
                        onAddressCleared={handleBranchAddressCleared}
                      />
                    )}

                    <div
                      className={
                        showMapPanel ? "flex flex-col gap-2 w-full" : "w-full"
                      }
                    >
                      <div className="">
                        <div style={{ margin: "12px" }}>
                          {showLocationPanel ? (
                            <LocationSelectWithPanel
                              key={"pickUpLocation"}
                              required={true}
                              className={"w-full"}
                              value={
                                homeDeliverySelected &&
                                !isPickupManuallySelected
                                  ? ""
                                  : pickupLocation
                              }
                              label={translate("pick up location")}
                              options={effectivePickupLocations || []}
                              disableSoftKeyboard={
                                !!(
                                  AppSettings?.notOpenKeyboardForLocation &&
                                  smallScreen
                                )
                              }
                              // Reuses BookingForm.getLocationOptionValue —
                              // same string shape getLocationByPickup/Dropoff +
                              // calendar matchers expect (CLAUDE.md §14).
                              getOptionValue={getLocationOptionValue}
                              getOptionLabel={getPickupLocationLabel}
                              getSortValue={getPickupDistanceSortValue}
                              anchorDate={reservationStartDate}
                              recentKey={"pickup"}
                              onChange={handlePickupChange}
                            />
                          ) : (
                            <AppInputSwitcher
                              required={true}
                              className={"w-full"}
                              value={
                                homeDeliverySelected &&
                                !isPickupManuallySelected
                                  ? ""
                                  : pickupDisplayValue
                              }
                              label={translate("pick up location")}
                              key={"pickUpLocation"}
                              options={effectivePickupLocations.map(
                                (e: Location) => {
                                  let icon = (
                                    <LocationCityIcon color="primary" />
                                  );
                                  if (e.isAirport) {
                                    icon = (
                                      <FlightTakeoffIcon htmlColor="blue" />
                                    );
                                  } else if (e.isRailway) {
                                    icon = <TrainIcon htmlColor="green" />;
                                  }
                                  return {
                                    label: getPickupLocationLabel(e),
                                    value: getLocationOptionValue(e),
                                    icon,
                                  };
                                }
                              )}
                              type="select"
                              variant="with-search"
                              onchange={handlePickupChange}
                            />
                          )}
                        </div>
                        {AppSettings?.locationReturnEnable &&
                          !homeReturnSelected && (
                            <div style={{ margin: "12px" }}>
                              <div className="min-w-fit max-w-fit">
                                <AppStandardMultiSelectCheckBox
                                  value={[
                                    isDroppingAtSameLocation
                                      ? translate("drop at same location")
                                      : "",
                                  ]}
                                  onChange={handleDropAtSameLocationChange}
                                  options={[translate("drop at same location")]}
                                  color={
                                    AppSettings?.secondaryAsBackgroundForButtons
                                      ? "secondaryColor"
                                      : "primary"
                                  }
                                />
                              </div>
                            </div>
                          )}
                        {showMapPanel &&
                          !isDroppingAtSameLocation &&
                          !isHomeReturnWithAddressActive && (
                            <div style={{ margin: "12px" }}>
                              <BranchAddressSearch
                                label={translate(
                                  "Search nearby drop-off branches"
                                )}
                                locations={
                                  locationDropOffDetail.length
                                    ? locationDropOffDetail
                                    : locationDetail
                                }
                                initialAddress={dropoffSearchAddress}
                                primaryColor={
                                  dynamicTheme?.palette?.["secondaryColor"]
                                    ?.main ?? undefined
                                }
                                secondaryColor={
                                  dynamicTheme?.palette?.["secondaryColor"]
                                    ?.main ?? undefined
                                }
                                onAddressSelected={handleDropoffAddressSelected}
                                onAddressCleared={handleDropoffAddressCleared}
                              />
                            </div>
                          )}
                        {isHomeReturnWithAddressActive ? (
                          <div style={{ margin: "12px" }}>
                            {showLocationPanel ? (
                              <LocationSelectWithPanel
                                key={"dropoff_return_reactive"}
                                required={true}
                                className="w-full"
                                value={
                                  !isDropoffManuallySelected
                                    ? ""
                                    : reservationDestination
                                }
                                label={translate("Dropoff Location")}
                                options={effectiveDropoffLocations || []}
                                getOptionValue={getLocationOptionValue}
                                getOptionLabel={getReturnOptionLabel}
                                getSortValue={getReturnDistanceSortValue}
                                anchorDate={reservationStartDate}
                                recentKey={"dropoff"}
                                onChange={handleReactiveDropoffChange}
                              />
                            ) : (
                              <AppInputSwitcher
                                required={true}
                                className="w-full"
                                value={
                                  !isDropoffManuallySelected
                                    ? ""
                                    : reservationDestination
                                }
                                label={translate("Dropoff Location")}
                                key={"dropoff_return_reactive"}
                                options={effectiveDropoffLocations.map((e) => {
                                  let icon = (
                                    <LocationCityIcon color="primary" />
                                  );
                                  if (e.isAirport) {
                                    icon = (
                                      <FlightTakeoffIcon htmlColor="blue" />
                                    );
                                  } else if (e.isRailway) {
                                    icon = <TrainIcon htmlColor="green" />;
                                  }
                                  return {
                                    label: getReturnOptionLabel(e),
                                    value: getLocationOptionValue(e),
                                    icon,
                                  };
                                })}
                                type="select"
                                variant="with-search"
                                onchange={handleReactiveDropoffChange}
                              />
                            )}
                          </div>
                        ) : !isDroppingAtSameLocation ? (
                          <div style={{ margin: "12px" }}>
                            {showLocationPanel ? (
                              <LocationSelectWithPanel
                                key={"dropOffLocation"}
                                required={true}
                                value={reservationDestination}
                                label={translate("drop off location")}
                                options={effectiveDropoffLocations || []}
                                getOptionValue={getLocationOptionValue}
                                getOptionLabel={getDropoffLocationLabel}
                                getSortValue={getDropoffDistanceSortValue}
                                anchorDate={reservationStartDate}
                                recentKey={"dropoff"}
                                onChange={handleDropoffChange}
                              />
                            ) : (
                              <AppInputSwitcher
                                required={true}
                                value={dropoffDisplayValue}
                                label={translate("drop off location")}
                                key={"dropOffLocation"}
                                options={effectiveDropoffLocations.map((e) => {
                                  let icon = (
                                    <LocationCityIcon color="primary" />
                                  );
                                  if (e.isAirport) {
                                    icon = (
                                      <FlightTakeoffIcon htmlColor="blue" />
                                    );
                                  } else if (e.isRailway) {
                                    icon = <TrainIcon htmlColor="green" />;
                                  }
                                  return {
                                    label: getDropoffLocationLabel(e),
                                    value: getLocationOptionValue(e),
                                    icon,
                                  };
                                })}
                                type="select"
                                variant="with-search"
                                onchange={handleDropoffChange}
                              />
                            )}
                          </div>
                        ) : null}
                      </div>
                      {/* ──────────────────────────────────────────────────────── */}
                      <div style={{ margin: "12px" }}>
                        {AppSettings?.showSeparatePickDropDate ? (
                          <>
                            {/* Start Date Picker */}
                            <DatePickerComponent
                              sx={{
                                svg: { color: "primary" },
                                input: { color: "primary" },
                                label: { color: "primary" },
                                backgroundColor: "primary",
                              }}
                              required={true}
                              disableOnDay={disableDate}
                              shouldDisableDateFn={pickupShouldDisableDate}
                              className="mb-4 w-full grow md:min-w-36"
                              label={pickUpDate}
                              value={reservationStartDate}
                              minDate={dayjs().add(minRentalAllowedDay, "day")}
                              maxDate={dayjs().add(365, "day")}
                              onChange={(selectedStartDate) => {
                                if (selectedStartDate) {
                                  const selectedLocation = locationDetail.find(
                                    (e) => {
                                      const optionValue =
                                        getLocationOptionValue(e);
                                      return (
                                        optionValue?.toLowerCase() ===
                                        pickupLocation?.toLowerCase()
                                      );
                                    }
                                  );

                                  if (
                                    selectedLocation &&
                                    selectedLocation.onlyDropOffOutOfHours &&
                                    Array.isArray(selectedLocation.closing)
                                  ) {
                                    const startDay = getDay(
                                      selectedStartDate.toDate()
                                    );

                                    const isDateClosed =
                                      selectedLocation.closing.some((entry) => {
                                        if (
                                          entry.dayOfTheWeek % 7 ===
                                          startDay
                                        ) {
                                          if (
                                            entry.validFrom &&
                                            entry.validTo
                                          ) {
                                            const validFrom = new Date(
                                              entry.validFrom
                                            );
                                            const validTo = new Date(
                                              entry.validTo
                                            );
                                            const selectedDate =
                                              selectedStartDate.toDate();

                                            return (
                                              selectedDate >= validFrom &&
                                              selectedDate <= validTo
                                            );
                                          }
                                          return true;
                                        }
                                        return false;
                                      });

                                    if (isDateClosed) {
                                      setDateError(
                                        "The date is not available for booking."
                                      );
                                      setStartDate(null);
                                      return;
                                    }
                                  }
                                  const openingObj =
                                    selectedLocation?.openings?.find(
                                      (e) =>
                                        getDay(selectedStartDate.toDate()) ===
                                        e.dayOfTheWeek % 7
                                    );
                                  const opening =
                                    selectedLocation?.isOutOfHours ||
                                    selectedLocation?.pickUpOutOfHours
                                      ? openingObj?.LocationStartTime
                                      : openingObj?.startTime;

                                  setStartDate(
                                    setTime(
                                      opening || "",
                                      selectedStartDate.toDate()
                                    )
                                  );
                                } else {
                                  setStartDate(null);
                                }
                              }}
                            />
                            {/* End Date Picker */}
                            <DatePickerComponent
                              sx={{
                                svg: { color: "primary" },
                                input: { color: "primary" },
                                label: { color: "primary" },
                                backgroundColor: "primary",
                              }}
                              className="w-full grow md:min-w-36"
                              required={true}
                              disableOnDay={disableDropoffDate}
                              shouldDisableDateFn={dropoffShouldDisableDate}
                              label={dropOffDate}
                              value={reservationEndDate}
                              minDate={
                                reservationStartDate
                                  ? dayjs(reservationStartDate)
                                  : dayjs().add(minRentalAllowedDay, "day")
                              }
                              maxDate={
                                reservationStartDate
                                  ? dayjs(reservationStartDate).add(
                                      maxRentalAllowedDay,
                                      "day"
                                    )
                                  : dayjs().add(365, "day")
                              }
                              onChange={(selectedEndDate) => {
                                if (selectedEndDate) {
                                  const selectedLocation = locationDetail.find(
                                    (e) => {
                                      const optionValue =
                                        getLocationOptionValue(e);
                                      return (
                                        optionValue?.toLowerCase() ===
                                        reservationDestination?.toLowerCase()
                                      );
                                    }
                                  );

                                  const opening =
                                    selectedLocation?.openings?.find(
                                      (e) =>
                                        getDay(selectedEndDate.toDate()) ===
                                        e.dayOfTheWeek % 7
                                    )?.startTime;

                                  setEndDate(
                                    setTime(
                                      opening || "",
                                      selectedEndDate.toDate()
                                    )
                                  );
                                } else {
                                  setEndDate(null);
                                }
                              }}
                            />
                          </>
                        ) : (
                          <AppInputSwitcher
                            disableOnDay={disableDate}
                            disableOnDayEnd={disableDropoffDate}
                            shouldDisableStartDateFn={pickupShouldDisableDate}
                            shouldDisableEndDateFn={dropoffShouldDisableDate}
                            required={true}
                            className="w-full grow md:min-w-72"
                            label={translate("reservation dates")}
                            key={"reservationDates"}
                            options={[]}
                            startDate={reservationStartDate}
                            endDate={reservationEndDate}
                            minDate={addDays(new Date(), minRentalAllowedDay)}
                            maxDate={
                              reservationStartDate
                                ? addDays(
                                    new Date(reservationStartDate?.toString()),
                                    maxRentalAllowedDay
                                  )
                                : addDays(new Date(), 365)
                            }
                            type="date"
                            variant="range"
                            onchange={(selectedDateRange) => {
                              if (selectedDateRange[0]) {
                                const selectedLocation = locationDetail.filter(
                                  (e) => {
                                    const optionValue =
                                      getLocationOptionValue(e);
                                    return (
                                      optionValue?.toLowerCase() ===
                                      pickupLocation?.toLowerCase()
                                    );
                                  }
                                )[0];

                                if (
                                  selectedLocation &&
                                  selectedLocation.onlyDropOffOutOfHours &&
                                  Array.isArray(selectedLocation.closing)
                                ) {
                                  const startDay = getDay(selectedDateRange[0]);

                                  const isDateClosed =
                                    selectedLocation.closing.some((entry) => {
                                      if (entry.dayOfTheWeek % 7 === startDay) {
                                        if (entry.validFrom && entry.validTo) {
                                          const validFrom = new Date(
                                            entry.validFrom
                                          );
                                          const validTo = new Date(
                                            entry.validTo
                                          );
                                          const selectedDate =
                                            selectedDateRange[0];

                                          return (
                                            selectedDate >= validFrom &&
                                            selectedDate <= validTo
                                          );
                                        }
                                        return true;
                                      }
                                      return false;
                                    });

                                  if (isDateClosed) {
                                    setDateError(
                                      "The date is not available for booking."
                                    );
                                    setStartDate(null);
                                    return;
                                  }
                                }

                                if (selectedLocation) {
                                  const opening =
                                    selectedLocation.openings.filter(
                                      (e) =>
                                        getDay(selectedDateRange[0]) ===
                                        e.dayOfTheWeek % 7
                                    )[0];

                                  const startTime = opening
                                    ? opening.LocationStartTime ||
                                      opening.startTime
                                    : "";

                                  setStartDate(
                                    setTime(startTime, selectedDateRange[0])
                                  );
                                }
                              } else {
                                setStartDate(null);
                              }
                              if (selectedDateRange[1]) {
                                const selectedLocation = locationDetail.filter(
                                  (e) => {
                                    const optionValue =
                                      getLocationOptionValue(e);
                                    return (
                                      optionValue?.toLowerCase() ===
                                      reservationDestination.toLowerCase()
                                    );
                                  }
                                )[0];

                                if (selectedLocation) {
                                  const opening =
                                    selectedLocation.openings.filter(
                                      (e) =>
                                        getDay(selectedDateRange[1]) ===
                                        e.dayOfTheWeek % 7
                                    )[0];

                                  const endTime = opening
                                    ? opening.LocationStartTime ||
                                      opening.startTime
                                    : "";

                                  setEndDate(
                                    setTime(endTime, selectedDateRange[1])
                                  );
                                }
                              } else {
                                setEndDate(null);
                              }
                            }}
                          />
                        )}
                      </div>
                      {dateError && (
                        <div className="text-red-500 text-sm mt-1">
                          {dateError}
                        </div>
                      )}
                      <div
                        style={{
                          margin: "12px",
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <AppInputSwitcher
                          required={true}
                          disabled={!reservationStartDate}
                          className="!w-[49%] min-w-[5rem]"
                          label={pickUpTime}
                          key={"pickUpTime"}
                          enableTimesInRange={getEnabledTimeSlots(
                            locationDetail,
                            pickupLocation,
                            reservationStartDate
                          )}
                          value={reservationStartDate}
                          options={[]}
                          type="time"
                          variant="slot"
                          onchange={(e) => {
                            setStartDate(e);
                          }}
                        />
                        <AppInputSwitcher
                          enableTimesInRange={getEnabledDropoffTimeSlots(
                            locationDropOffDetail || locationDetail,
                            reservationDestination,
                            reservationEndDate
                          )}
                          disabled={!reservationEndDate}
                          required={true}
                          className="!w-[49%] min-w-[5rem]"
                          label={dropOffTime}
                          key={"dropOffTime"}
                          options={[]}
                          value={reservationEndDate}
                          type="time"
                          variant="slot"
                          onchange={(e) => {
                            setEndDate(e);
                          }}
                        />
                      </div>
                      {AppSettings?.showMacroGroupSelection && (
                        <AppInputSwitcher
                          required={false}
                          className={"w-full"}
                          type="select"
                          variant="with-search"
                          label={translate("Macro Group")}
                          options={macroGroupOptions?.map((e) => e.label) || []}
                          value={selectedMacroGroup}
                          onchange={(e) => {
                            const selectedOption = macroGroupOptions?.find(
                              (option) => option.label === e
                            );
                            if (selectedOption) {
                              setSelectedMacroGroup(selectedOption.label);
                              setSelectedMacroGroupWebCode(
                                selectedOption.WebCode
                              );
                            }
                          }}
                        />
                      )}
                      {disableCoupon === false &&
                        (mountedRef.current &&
                        reservationSourceEnabled === true &&
                        reservationSourceForUser?.length > 0 ? (
                          <div className="flex" style={{ margin: "12px" }}>
                            <AppInputSwitcher
                              required={true}
                              className="w-full min-w-[12rem]"
                              label={
                                userId
                                  ? bookingResSourceForReservedArea
                                  : bookingReservationSource
                              }
                              key={"reservationPlatform"}
                              options={reservationSourceForUser?.map(
                                (e) =>
                                  e["description"] ||
                                  e["Ragione Sociale textRS"] ||
                                  e["Ragione Sociale"]
                              )}
                              type="select"
                              variant="with-search"
                              onchange={(e) => {
                                setReservationPlatform(e);
                                const selectedObj =
                                  reservationSourceForUser.find(
                                    (item) =>
                                      item["description"] === e ||
                                      item["Ragione Sociale textRS"] === e ||
                                      item["Ragione Sociale"]
                                  );
                                setReservedAreaPayNow(
                                  selectedObj?.payNow ?? false
                                );
                              }}
                              value={reservationPlatform}
                            />
                          </div>
                        ) : (
                          AppSettings?.showReservationGroupSelection &&
                          !isScriptChannelResSource &&
                          !reservationSourceEnabled && (
                            <div className="flex" style={{ margin: "12px" }}>
                              <AppInputSwitcher
                                required={true}
                                className="w-full min-w-[12rem]"
                                label={bookingReservationSource}
                                key={"reservationPlatform"}
                                options={reservationSourceOptions?.map(
                                  (e) =>
                                    e["Ragione Sociale textRS"] ||
                                    e["Ragione Sociale"]
                                )}
                                value={reservationPlatform}
                                type="select"
                                variant="with-search"
                                onchange={(e) => {
                                  setReservationPlatform(e);
                                  const selectedObj =
                                    reservationSourceOptions.find(
                                      (item) =>
                                        item["Ragione Sociale"] === e ||
                                        item["Ragione Sociale textRS"] === e
                                    );
                                  setReservedAreaPayNow(
                                    selectedObj?.payNow ?? false
                                  );
                                }}
                              />
                            </div>
                          )
                        ))}{" "}
                      {AppSettings?.showWebCodeInput && (
                        <div style={{ margin: "12px" }}>
                          <AppInputSwitcher
                            required={false}
                            autoComplete="off"
                            className="!w-[49%] min-w-[5rem]"
                            label={codiceLabel}
                            key={"Voucher Code"}
                            placeholder={codiceLabel}
                            options={[]}
                            value={agreementCoupon}
                            type="text"
                            variant="slot"
                            disabledCoupon={disableCoupon}
                            onchange={(e) => {
                              setagreementCoupon(e);
                            }}
                          />
                        </div>
                      )}
                      {showModal && validateAgreementCoupon && (
                        <div
                          className="modal-overlay"
                          style={{
                            position: "fixed",
                            top: "0",
                            left: "0",
                            right: "0",
                            bottom: "0",
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            zIndex: "1001",
                          }}
                        >
                          <div
                            className="modal-content"
                            style={{
                              background: "white",
                              width: "300px",
                              padding: "15px",
                              borderRadius: "8px",
                              textAlign: "center",
                              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                              zIndex: "1000",
                            }}
                          >
                            <p>{modalMessage}</p>
                            <button
                              onClick={() => setShowModal(false)}
                              className="close-button"
                              style={{
                                backgroundColor:
                                  dynamicTheme?.palette["primary"]?.main,
                                color: "white",
                                border: "none",
                                padding: "10px 20px",
                                borderRadius: "4px",
                                cursor: "pointer",
                                marginTop: "10px",
                              }}
                            >
                              {translate("Close")}
                              {/*Close*/}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    <div
                      className={
                        showMapPanel ? "gap-2 w-full" : "gap-2 w-full ml-[30px]"
                      }
                    >
                      <div>
                        {Object.entries(ageCriteriaData)
                          .map(([key, value]) => {
                            switch (key.toUpperCase()) {
                              case "YOUNGDRIVER":
                              case "YOUNGDRIVER2":
                              case "NOAGEDRIVER":
                              case "SENIORDRIVER":
                                return (
                                  <div
                                    key={key}
                                    style={{ marginBottom: "6px" }}
                                  >
                                    <AppRadioButton
                                      required={true}
                                      color={
                                        AppSettings?.secondaryAsBackgroundForButtons
                                          ? "secondaryColor"
                                          : "primary"
                                      }
                                      value={isAgeLimitSelected}
                                      className="w-full text-xs"
                                      onChange={(e) => {
                                        setIsAgeLimitSelected(e);
                                      }}
                                      options={[
                                        {
                                          label: t(value),
                                          value: key,
                                        },
                                      ]}
                                    />
                                  </div>
                                );
                              default:
                                return null;
                            }
                          })
                          .filter((option) => option !== null)}
                      </div>
                      <div style={showMapPanel ? {} : { marginRight: "20px" }}>
                        <AppButton
                          type={"submit"}
                          endIcon={<SearchIcon />}
                          className="w-[100%] h-14 w-full"
                          color={
                            AppSettings?.secondaryAsBackgroundForButtons
                              ? "secondaryColor"
                              : "primary"
                          }
                          style={{
                            borderRadius: `${
                              AppSettings?.buttonBorderRadius || 0
                            }px`,
                          }}
                        >
                          <p className="w-full text-center">
                            {translate("Find vehicles")}
                          </p>
                        </AppButton>
                      </div>
                      {AppSettings?.showTextBeginningOfBookingForm && (
                        <div className="w-full">
                          <p
                            className="self-start"
                            style={{
                              color:
                                dynamicTheme?.palette["secondaryColor"]?.main,
                            }}
                          >
                            {showTextBeginningOfBookingFormMsg}
                          </p>
                        </div>
                      )}
                    </div>
                  </>
                </AppCard>
              </div>
              {showMapPanel && (
                <div
                  className="w-full md:flex-1 md:min-w-0 md:self-stretch flex flex-col border-t border-gray-100 md:border-t-0 md:border-l"
                  style={{
                    overflow: "hidden",
                    minHeight: isDroppingAtSameLocation ? 420 : 560,
                  }}
                >
                  {/* Pickup map */}
                  <div
                    style={{
                      flex: isDroppingAtSameLocation ? "1 1 100%" : "1 1 50%",
                      minHeight: isDroppingAtSameLocation ? 420 : 280,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <div
                      style={{
                        padding: "6px 12px",
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#555",
                        background: "#f8f8f8",
                        borderBottom: "1px solid #e0e0e0",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        flexShrink: 0,
                      }}
                    >
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          background:
                            dynamicTheme?.palette?.["primary"]?.main ??
                            "#E24B4A",
                          display: "inline-block",
                          flexShrink: 0,
                        }}
                      />
                      {translate("Pickup Location")}
                      {isDroppingAtSameLocation && (
                        <span>
                          &nbsp;&amp; {translate("Drop-off Location")}
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        flex: 1,
                        minHeight: isDroppingAtSameLocation ? 420 : 280,
                      }}
                    >
                      <BranchMapPanel
                        mapState={mapPanelState}
                        allLocations={locationDetail}
                        rankedBranches={branchRankedLocations}
                        addressCoords={branchAddressCoords}
                        selectedBranch={selectedBranchForMap}
                        primaryColor={
                          dynamicTheme?.palette?.["primary"]?.main ?? "#E24B4A"
                        }
                        minHeight={isDroppingAtSameLocation ? 420 : 280}
                      />
                    </div>
                  </div>
                  {!isDroppingAtSameLocation && (
                    <div
                      style={{
                        flex: "1 1 50%",
                        minHeight: 280,
                        borderTop: "2px solid #e0e0e0",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <div
                        style={{
                          padding: "6px 12px",
                          fontSize: 12,
                          fontWeight: 600,
                          color: "#555",
                          background: "#f8f8f8",
                          borderBottom: "1px solid #e0e0e0",
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          flexShrink: 0,
                        }}
                      >
                        <span
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background:
                              dynamicTheme?.palette?.["secondaryColor"]?.main ??
                              "#1860ab",
                            display: "inline-block",
                            flexShrink: 0,
                          }}
                        />
                        {translate("Drop-off Location")}
                      </div>
                      <div style={{ flex: 1, minHeight: 280 }}>
                        <BranchMapPanel
                          mapState={effectiveDropoffMapState}
                          allLocations={locationDetail}
                          rankedBranches={dropoffRankedLocations}
                          addressCoords={dropoffAddressCoords}
                          selectedBranch={effectiveDropoffSelectedBranch}
                          primaryColor={
                            dynamicTheme?.palette?.["secondaryColor"]?.main ??
                            "#1860ab"
                          }
                          minHeight={280}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </form>
      </main>
    </ThemeProvider>
  );
}

export default BookingForm;
